import { useState, useEffect, useLayoutEffect, useRef, useCallback, useMemo, Fragment } from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import axios from "axios";
import { Send, CheckCircle, ArrowLeft, Sparkles, AlertCircle, Mic, MicOff, Volume2, VolumeX, X, Paperclip, Phone, PhoneOff, RotateCcw, Clock, FileText, User, Mail, MapPin, MessageSquare, ClipboardCheck, Edit } from "lucide-react";
import ContactOverlay from "@/components/ContactOverlay";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ── Kill switches for credit-burning timers — keep these at false (v9.1) ──
// The stall poller and post-quick-reply nudge auto-send "Please continue" on the
// user's behalf, which caused the death-loop / credit-burn issues. Disabled.
const STALL_POLLER_ENABLED = false;
const QUICK_REPLY_NUDGE_ENABLED = false;

// Live voice ("Talk to Sasha") is gated behind this flag for launch. OFF = text+dictation only.
// This is the kill switch — everything live-voice keys off it.
const VOICE_CALL_ENABLED = String(process.env.REACT_APP_VOICE_CALL_ENABLED).toLowerCase() === "true";

// Render field values — shows [UNCLEAR] as friendly "To be confirmed" instead of raw tag
const FieldValue = ({ value, fallback = "Not provided" }) => {
  if (!value) return <span className="text-muted-foreground/60 italic">{fallback}</span>;
  if (value.includes("[UNCLEAR]")) {
    const cleaned = value.replace(/\[UNCLEAR\]/g, "").replace(/\s*—\s*$/, "").trim();
    return (
      <span className="inline-flex items-center gap-1.5">
        {cleaned && <span>{cleaned}</span>}
        <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-amber-500/15 text-amber-400 border border-amber-500/25">
          To be confirmed on call
        </span>
      </span>
    );
  }
  return <span>{value}</span>;
};

// Strip [UNCLEAR] from visible chat messages — show friendly text instead
const cleanMessageText = (text) => {
  if (!text) return text;
  return text.replace(/\[UNCLEAR\]/g, "to be confirmed on call");
};

export default function ChatInterface() {
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sessionId, setSessionId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [starting, setStarting] = useState(true);
  const [intakeReady, setIntakeReady] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [error, setError] = useState(null);
  const [msgCount, setMsgCount] = useState(0);
  const [modeChoice, setModeChoice] = useState(null); // null = choosing, "voice" = always-on, "text" = typing
  // Dictation onboarding popup — shown once before the user starts interacting with Sasha
  const [dictPromptDismissed, setDictPromptDismissed] = useState(
    () => sessionStorage.getItem("dictPromptSeen") === "1"
  );
  const [audioEnabled, setAudioEnabled] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [liveVoice, setLiveVoice] = useState(false);
  const [liveStatus, setLiveStatus] = useState("idle");
  const [progress, setProgress] = useState({ percentage: 0, active_phase: 0, phases: [
    { name: "Contact Info", total: 3, done: 0, complete: false },
    { name: "Location", total: 2, done: 0, complete: false },
    { name: "Incident Details", total: 4, done: 0, complete: false },
    { name: "Call & Fee", total: 2, done: 0, complete: false },
    { name: "Final Review", total: 1, done: 0, complete: false },
  ]});
  const [micPermission, setMicPermission] = useState("unknown");
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [uiCard, setUiCard] = useState(null);
  // Dictation (speech-to-text into the text box) — separate from the live "Call Sasha" voice call.
  const [dictating, setDictating] = useState(false);       // mic actively recording a dictation clip
  const [transcribing, setTranscribing] = useState(false); // clip sent, waiting on the transcript

  const formatPhone = (raw) => {
    if (!raw) return raw;
    const digits = String(raw).replace(/\D/g, '');
    const cleaned = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
    if (cleaned.length < 10) return raw;
    return `(${cleaned.slice(0, 3)})-${cleaned.slice(3, 6)}-${cleaned.slice(6, 10)}`;
  };

  const [contactData, setContactData] = useState(null);
  const [contactConfirmed, setContactConfirmed] = useState(false);
  const [editingConfirmedContact, setEditingConfirmedContact] = useState(false);
  const [editingContact, setEditingContact] = useState({});
  const [showContactOverlay, setShowContactOverlay] = useState(false);
  const pendingOverlayContact = useRef(null);
  const [caseSummaryData, setCaseSummaryData] = useState(null);
  const [caseSummaryConfirmed, setCaseSummaryConfirmed] = useState(false);
  const [editingCaseSummary, setEditingCaseSummary] = useState({});
  const [caseSummaryEditMode, setCaseSummaryEditMode] = useState(false);
  const [showStartOverConfirm, setShowStartOverConfirm] = useState(false);
  const [caseSummaryCardMsgIndex, setCaseSummaryCardMsgIndex] = useState(-1);
  const [summaryData, setSummaryData] = useState(null);
  const [editingSummary, setEditingSummary] = useState({});
  const [showSummaryOverlay, setShowSummaryOverlay] = useState(false);
  // ── Phase 5 (v9.1): inline Final Review + Submission Confirmation cards ──
  const [finalReviewData, setFinalReviewData] = useState(null);
  const [editingFinalReview, setEditingFinalReview] = useState(null);
  const [finalReviewEditSection, setFinalReviewEditSection] = useState(null);
  const [submissionData, setSubmissionData] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const abandonTimerRef = useRef(null);
  const lastActivityRef = useRef(Date.now());

  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const fileInputRef = useRef(null);
  const currentAudioRef = useRef(null);
  const sessionIdRef = useRef(null);
  const chatContainerRef = useRef(null);
  const heartbeatRef = useRef(null);
  // WebRTC refs
  const pcRef = useRef(null);
  const dcRef = useRef(null);
  const audioElRef = useRef(null);
  const liveStreamRef = useRef(null);
  const voiceMessagesRef = useRef([]);
  const callTimerRef = useRef(null); // hard per-call duration cap — OpenAI Realtime bills per minute
  const MAX_CALL_MS = 8 * 60 * 1000; // 8-minute hard cap per live voice call
  // Dictation refs (text-box speech-to-text)
  const mediaRecorderRef = useRef(null);
  const dictationChunksRef = useRef([]);
  const dictationStreamRef = useRef(null);
  const sendAfterTranscribeRef = useRef(false); // set when the user hits Send mid-dictation: stop → transcribe → send
  // ── Hybrid dictation: free browser Web Speech API streams words live; Whisper stays the source of truth ──
  const recognitionRef = useRef(null);        // live Web Speech recognizer instance (null when unused/unsupported)
  const dictationBaseRef = useRef("");        // text already in the box when dictation began — live preview + Whisper append to THIS
  const liveTranscriptRef = useRef("");       // best-effort transcript heard by Web Speech (fallback if Whisper fails/empty)
  const webSpeechActiveRef = useRef(false);   // true when Web Speech drove this dictation (read in onstop, survives the stop)
  const stoppingDictationRef = useRef(false); // true during an intentional stop so recognizer.onend doesn't auto-restart
  const interruptTimerRef = useRef(null); // noise gate: only cancel after 350ms of sustained speech
  const stallTimerRef = useRef(null); // post-quick-reply stall recovery
  const stallNudgeCountRef = useRef(0); // consecutive nudge fires — stops after max
  const intakeReadyRef = useRef(false); // mirror of intakeReady for use inside timers
  const loadingRef = useRef(false);     // mirror of loading for use inside timers
  const lastSashaResponseRef = useRef(Date.now()); // tracks when Sasha last sent a message
  const lastPhaseCompleteRef = useRef(null); // last [PHASE_COMPLETE: N] received from backend
  const contactCardAnchorLockedRef = useRef(false); // once set, card never re-anchors to a later message
  const caseSummaryCardAnchorLockedRef = useRef(false); // same pattern for case summary card
  // uiMode — single source of truth for what the UI is allowed to do right now
  // "chatting"     → Sasha talks, timers run, input open
  // "card:contact" → contact card up, all timers blocked, input locked
  // "card:summary" → case summary card up, all timers blocked, input locked
  const [uiMode, setUiMode] = useState("chatting");
  const uiModeRef = useRef("chatting"); // mirror for timer callbacks

  // Normalize summary data so dropdown values match exactly
  const normalizeSummary = (data) => {
    if (!data) return data;
    const d = { ...data };
    // Normalize availability to match dropdown options
    if (d.availability) {
      const a = d.availability.toLowerCase().trim();
      if (a.includes("early morning") || a.includes("7am") || a.includes("7 am")) d.availability = "Early Morning (7am–9am)";
      else if (a.includes("late afternoon") || a.includes("3pm") || a.includes("4pm") || a.includes("5pm")) d.availability = "Late Afternoon (3pm–6pm)";
      else if (a.includes("early afternoon") || a.includes("12pm") || a.includes("1pm") || a.includes("2pm")) d.availability = "Early Afternoon (12pm–3pm)";
      else if (a.includes("morning") || a.includes("9am") || a.includes("10am") || a.includes("11am")) d.availability = "Morning (9am–12pm)";
      else if (a.includes("evening") || a.includes("night") || a.includes("6pm") || a.includes("7pm") || a.includes("8pm")) d.availability = "Evening (6pm–9pm)";
      else if (a.includes("any") || a.includes("flexible")) d.availability = "Flexible";
    }
    // Normalize prior_consultation to Yes/No
    if (d.prior_consultation) {
      const p = d.prior_consultation.toLowerCase().trim();
      if (p === "no" || p.includes("haven") || p.includes("not")) d.prior_consultation = "No";
      else if (p === "yes" || p.includes("have") || p.includes("did")) d.prior_consultation = "Yes";
    }
    // Normalize fee_type to match dropdown options
    if (d.fee_type) {
      const f = d.fee_type.toLowerCase().trim();
      if (f.includes("contingen")) d.fee_type = "Contingency";
      else if (f.includes("flat")) d.fee_type = "Flat Rate";
      else if (f.includes("hour")) d.fee_type = "Hourly";
      else if (f.includes("not sure") || f.includes("don't know")) d.fee_type = "Not Sure";
    }
    // Format phone number
    if (d.phone) d.phone = formatPhone(d.phone);
    return d;
  };

  // ── Session init ────────────────────────────────────────────
  useEffect(() => {
    const existing = localStorage.getItem("intakeSessionId");
    if (existing) loadExistingSession(existing);
    else startNewChat();

    // ── LOCK the page: prevent iOS Safari from scrolling body when input is focused ──
    const html = document.documentElement;
    const body = document.body;
    html.style.position = 'fixed';
    html.style.overflow = 'hidden';
    html.style.width = '100%';
    html.style.height = '100%';
    body.style.position = 'fixed';
    body.style.overflow = 'hidden';
    body.style.width = '100%';
    body.style.height = '100%';
    body.style.top = '0';
    body.style.left = '0';

    // ── visualViewport: track actual visible height for keyboard handling ──
    const vv = window.visualViewport;
    const updateHeight = () => {
      const h = vv ? vv.height : window.innerHeight;
      html.style.setProperty('--app-height', `${h}px`);
      // Kill any page scroll iOS tries to do
      window.scrollTo(0, 0);
      // Scroll messages to bottom so latest text is visible
      requestAnimationFrame(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'instant' });
      });
    };
    updateHeight();
    if (vv) {
      vv.addEventListener('resize', updateHeight);
      vv.addEventListener('scroll', updateHeight);
    }
    window.addEventListener('resize', updateHeight);

    return () => {
      html.style.position = '';
      html.style.overflow = '';
      html.style.width = '';
      html.style.height = '';
      body.style.position = '';
      body.style.overflow = '';
      body.style.width = '';
      body.style.height = '';
      body.style.top = '';
      body.style.left = '';
      html.style.removeProperty('--app-height');
      if (vv) {
        vv.removeEventListener('resize', updateHeight);
        vv.removeEventListener('scroll', updateHeight);
      }
      window.removeEventListener('resize', updateHeight);
    };
  }, []);

  // ── Heartbeat every 30s ─────────────────────────────────────
  useEffect(() => {
    if (!sessionId) return;
    heartbeatRef.current = setInterval(() => {
      axios.post(`${API}/chat/${sessionId}/heartbeat`).catch(() => {});
    }, 30000);
    return () => clearInterval(heartbeatRef.current);
  }, [sessionId]);

  // Keep refs in sync with state so timer callbacks see current values
  useEffect(() => { intakeReadyRef.current = intakeReady; }, [intakeReady]);
  useEffect(() => { loadingRef.current = loading; }, [loading]);
  useEffect(() => { uiModeRef.current = uiMode; }, [uiMode]);

  // Phase-specific nudge messages — sent when stall recovery fires after a known phase transition
  const PHASE_NUDGES = {
    0: "Phase 1 contact info is confirmed. Please ask the client where the incident took place and whether they have consulted an attorney before.",
    1: "Phase 2 location is confirmed. Please ask the client to describe in detail what happened.",
    2: "Phase 3 incident details are confirmed. Please ask the client when they are available for a call and what fee structure they prefer.",
    3: "Phase 4 call and fee preferences are confirmed. Please provide a complete structured summary of all information collected.",
  };
  const getStallNudge = () => PHASE_NUDGES[lastPhaseCompleteRef.current] ?? "";
  const setPhaseComplete = (phase, sid) => {
    lastPhaseCompleteRef.current = phase;
    sessionStorage.setItem(`phaseComplete_${sid}`, phase);
  };

  // ── Stall recovery polling: if Sasha goes quiet for 40s, nudge her forward ──
  // Max 3 consecutive nudges — if nothing changes after 3 fires, stop spamming.
  // Counter resets whenever the user sends a real message.
  useEffect(() => {
    if (!sessionId) return;
    if (!STALL_POLLER_ENABLED) return; // ← kill switch (v9.1): no auto-nudges
    const MAX_NUDGES = 3;
    const interval = setInterval(async () => {
      if (uiModeRef.current !== "chatting" || intakeReadyRef.current || loadingRef.current) return;
      if (inputRef.current?.value?.trim()) return; // user is mid-typing
      if (stallNudgeCountRef.current >= MAX_NUDGES) return; // death-loop guard
      const idleMs = Date.now() - lastSashaResponseRef.current;
      if (idleMs < 40000) return;
      stallNudgeCountRef.current += 1;
      lastSashaResponseRef.current = Date.now(); // reset so we don't spam
      const nudgeMsg = getStallNudge();
      loadingRef.current = true; setLoading(true);
      try {
        const nudge = await axios.post(`${API}/chat/message`, {
          session_id: sessionIdRef.current,
          message: nudgeMsg
        });
        lastSashaResponseRef.current = Date.now();
        if (nudge.data.phase_complete != null) setPhaseComplete(nudge.data.phase_complete, sessionIdRef.current);
        setMessages(prev => [...prev, { role: "assistant", content: nudge.data.message, timestamp: new Date().toISOString() }]);
        if (nudge.data.progress) setProgress(nudge.data.progress);
        if (nudge.data.intake_ready) { intakeReadyRef.current = true; setIntakeReady(true); }
        if (nudge.data.ui_card) setUiCard(nudge.data.ui_card); else setUiCard(null);
        if (nudge.data.case_summary_data && !caseSummaryConfirmed) {
          uiModeRef.current = "card:summary";
          setUiMode("card:summary");
          setCaseSummaryData(nudge.data.case_summary_data);
          setEditingCaseSummary({ ...nudge.data.case_summary_data });
        }
      } catch (e) {
      } finally {
        loadingRef.current = false; setLoading(false);
      }
    }, 5000); // check every 5s
    return () => clearInterval(interval);
  }, [sessionId, caseSummaryConfirmed]);

  // ── Abandon tracking on page unload ─────────────────────────
  useEffect(() => {
    const handleUnload = () => {
      const sid = sessionIdRef.current;
      if (sid) {
        // Use sendBeacon for reliable unload tracking
        navigator.sendBeacon(`${API}/chat/${sid}/abandon`);
      }
    };
    window.addEventListener("beforeunload", handleUnload);
    return () => window.removeEventListener("beforeunload", handleUnload);
  }, []);

  useEffect(() => { scrollToBottom(); }, [messages, loading]);

  // ── Check mic permission on mount ───────────────────────────
  useEffect(() => {
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      setMicPermission("unavailable");
      return;
    }
    if (navigator.permissions) {
      const toState = (s) => s === "granted" ? "granted" : s === "denied" ? "denied" : s === "prompt" ? "prompt" : "unknown";
      navigator.permissions.query({ name: "microphone" }).then((result) => {
        setMicPermission(toState(result.state));
        result.onchange = () => setMicPermission(toState(result.state));
      }).catch(() => setMicPermission("prompt")); // treat query failure as "not yet asked"
    } else {
      // Browser doesn't support permissions API (some Safari versions) — show button and let getUserMedia decide
      setMicPermission("prompt");
    }
  }, []);

  // ── (v9.1) Old summary overlay auto-open removed — Phase 5 now uses the inline
  //    FinalReviewCard + SubmissionConfirmationCard instead of the modal overlay. ──

  // ── Auto-scroll when any confirmation card appears so it's never missed ──────
  useEffect(() => {
    if (contactData || caseSummaryData) {
      setTimeout(() => scrollToBottom(), 100);
    }
  }, [contactData, caseSummaryData]);

  // Track user activity
  const trackActivity = useCallback(() => {
    lastActivityRef.current = Date.now();
  }, []);

  useEffect(() => {
    window.addEventListener("mousemove", trackActivity);
    window.addEventListener("keydown", trackActivity);
    window.addEventListener("touchstart", trackActivity);
    return () => {
      window.removeEventListener("mousemove", trackActivity);
      window.removeEventListener("keydown", trackActivity);
      window.removeEventListener("touchstart", trackActivity);
    };
  }, [trackActivity]);

  // Deduplicate: keep only the first assistant message before any user message
  const displayMessages = useMemo(() => {
    if (messages.length <= 1) return messages;
    const result = [messages[0]];
    let seenUser = messages[0].role === "user";
    for (let i = 1; i < messages.length; i++) {
      if (!seenUser && messages[i].role === "assistant") continue;
      if (messages[i].role === "user") seenUser = true;
      result.push(messages[i]);
    }
    return result;
  }, [messages]);

  // Anchor the contact card inline — locked to the last message at the moment contactData first appears
  // Index-locking is more reliable than phrase-matching: works regardless of how Sasha words the acknowledgment
  const [contactCardMsgIndex, setContactCardMsgIndex] = useState(-1);
  useLayoutEffect(() => {
    if (!contactData) {
      contactCardAnchorLockedRef.current = false;
      setContactCardMsgIndex(-1);
      return;
    }
    if (contactCardAnchorLockedRef.current) return; // already pinned — don't re-anchor
    const idx = displayMessages.length - 1;
    if (idx >= 0) {
      contactCardAnchorLockedRef.current = true;
      setContactCardMsgIndex(idx);
    }
  }, [contactData, displayMessages]);

  // Anchor the case summary card inline — same index-lock pattern as contact card
  useLayoutEffect(() => {
    if (!caseSummaryData) {
      caseSummaryCardAnchorLockedRef.current = false;
      setCaseSummaryCardMsgIndex(-1);
      return;
    }
    if (caseSummaryCardAnchorLockedRef.current) return; // already pinned
    const idx = displayMessages.length - 1;
    if (idx >= 0) {
      caseSummaryCardAnchorLockedRef.current = true;
      setCaseSummaryCardMsgIndex(idx);
    }
  }, [caseSummaryData, displayMessages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const loadExistingSession = async (sid) => {
    try {
      const res = await axios.get(`${API}/chat/${sid}`);
      if (res.data.status === "completed") {
        localStorage.removeItem("intakeSessionId"); startNewChat(); return;
      }
      // Abandoned means the user refreshed or closed the tab — reactivate and resume
      if (res.data.status === "abandoned") {
        axios.post(`${API}/chat/${sid}/reactivate`).catch(() => {});
      }
      setSessionId(sid); sessionIdRef.current = sid;
      // Restore lastPhaseCompleteRef from sessionStorage so stall nudges stay smart after reload
      const savedPhase = sessionStorage.getItem(`phaseComplete_${sid}`);
      if (savedPhase != null) {
        lastPhaseCompleteRef.current = parseInt(savedPhase, 10);
      }
      const msgs = res.data.messages || [];
      const userMsgCount = msgs.filter(m => m.role === "user").length;

      // Resume — just show existing messages, no hardcoded nudge
      setMessages(msgs.map(m => ({ role: m.role, content: m.content, timestamp: m.timestamp })));
      setMsgCount(userMsgCount);
      if (res.data.progress) setProgress(res.data.progress);
      // Restore contact confirmation state
      if (res.data.contact_confirmed) { setContactConfirmed(true); setShowContactOverlay(false); }
      // Restore case summary and intake ready state from /fields
      try {
        const fieldsRes = await axios.get(`${API}/chat/${sid}/fields`);
        const f = fieldsRes.data;

        // ── Restore contact card on reload ──────────────────────────────────────
        // If the contact card was confirmed, show the confirmed (read-only) card
        // If not yet confirmed but we have contact data, re-show the pending card
        if (f.cards?.contact_confirmed && f.confirmed_contact) {
          setContactData(f.confirmed_contact);
          setEditingContact({ ...f.confirmed_contact, phone: formatPhone(f.confirmed_contact.phone) });
          setContactConfirmed(true);
        } else if (!f.cards?.contact_confirmed && f.contact_data) {
          setContactData(f.contact_data);
          setEditingContact({ ...f.contact_data, phone: formatPhone(f.contact_data.phone) });
        }

        if (f.cards?.case_summary_confirmed) {
          setCaseSummaryConfirmed(true);
        }
        // Restore case summary card data from tags on reload — both confirmed and pending states
        if ((f.progress_pct >= 90 || f.cards?.case_summary_confirmed) && f.tags) {
          const rebuilt = {
            fact_summary: f.tags.tag_incident_summary || "",
            location: f.tags.tag_location || "",
            case_category: f.tags.tag_case_category || "",
            q1_answer: f.tags.tag_q1_text || "",
            q2_answer: f.tags.tag_q2_text || "",
            q3_answer: f.tags.tag_q3_text || "",
          };
          if (rebuilt.fact_summary || rebuilt.q1_answer) {
            setCaseSummaryData(rebuilt);
            setEditingCaseSummary({ ...rebuilt });
            if (!f.cards?.case_summary_confirmed) {
              uiModeRef.current = "card:summary";
              setUiMode("card:summary");
            }
          }
        }
        // Restore uiMode: contact card pending = card:contact, chatting otherwise
        if (!f.cards?.contact_confirmed && f.contact_data) {
          uiModeRef.current = "card:contact";
          setUiMode("card:contact");
        }
        // Check if intake was already ready
        if (f.fields?.includes("summary_given") || f.progress_pct >= 100) setIntakeReady(true);
      } catch {
        // /fields call failed — fall back to message-scan for intake_ready
        const hasIntakeReady = msgs.some(m => m.role === "assistant" && (m.content?.includes("[INTAKE_READY]") || m.content?.includes("[USER_WRAPPING]")));
        if (hasIntakeReady) setIntakeReady(true);
      }
      setStarting(false);
    } catch { localStorage.removeItem("intakeSessionId"); startNewChat(); }
  };

  const startNewChat = async () => {
    setStarting(true); setError(null);
    try {
      const res = await axios.post(`${API}/chat/start`, {
        consent_timestamp: new Date().toISOString()
      });
      const sid = res.data.session_id;
      setSessionId(sid); sessionIdRef.current = sid;
      localStorage.setItem("intakeSessionId", sid);
      lastPhaseCompleteRef.current = null;
      sessionStorage.removeItem(`phaseComplete_${sid}`);
      setMessages([{ role: "assistant", content: res.data.message, timestamp: new Date().toISOString() }]);
      if (res.data.progress) setProgress(res.data.progress);
      // If user confirmed the overlay before the session was ready, send it now
      if (pendingOverlayContact.current) {
        try {
          await axios.post(`${API}/chat/${sid}/confirm-contact`, pendingOverlayContact.current);
          setContactConfirmed(true);
          setContactData(null);
          pendingOverlayContact.current = null;
        } catch (e) {
        }
      }
    } catch { setError("Unable to connect. Please try again."); }
    finally { setStarting(false); }
  };

  const resetConversation = async () => {
    setShowResetConfirm(false);
    if (liveVoice) stopLiveVoice();
    // Reset ALL session-bound state so stale values don't bleed into the new session
    setIntakeReady(false);
    setMsgCount(0);
    setInput("");
    setError(null);
    setContactData(null);
    setContactConfirmed(false);
    setEditingConfirmedContact(false);
    setEditingContact({});
    setCaseSummaryData(null);
    setCaseSummaryConfirmed(false);
    setEditingCaseSummary({});
    setSummaryData(null);
    setEditingSummary({});
    setShowSummaryOverlay(false);
    setModeChoice(null);
    uiModeRef.current = "chatting";
    setUiMode("chatting");
    localStorage.removeItem("intakeSessionId");
    await startNewChat();
  };

  const playBase64Audio = (base64) => {
    try {
      if (currentAudioRef.current) currentAudioRef.current.pause();
      const audio = new Audio(`data:audio/mp3;base64,${base64}`);
      currentAudioRef.current = audio;
      audio.play().catch(() => {});
    } catch {}
  };

  const playTextAsSpeech = async (text) => {
    if (!audioEnabled) return;
    try {
      if (currentAudioRef.current) currentAudioRef.current.pause();
      const res = await axios.post(`${API}/chat/tts`, { text, voice: "nova" });
      if (res.data.audio_base64) playBase64Audio(res.data.audio_base64);
    } catch {}
  };

  const sendMessage = async (overrideText) => {
    const msgText = overrideText || input.trim();
    if (!msgText || loading || !sessionId) return;
    // Clear any pending stall-recovery timer — user is actively sending
    if (stallTimerRef.current) { clearTimeout(stallTimerRef.current); stallTimerRef.current = null; }
    stallNudgeCountRef.current = 0; // reset death-loop guard on real user message
    setInput(""); setError(null); setUiCard(null);
    setMessages(prev => [...prev, { role: "user", content: msgText, timestamp: new Date().toISOString() }]);
    setMsgCount(c => c + 1); setLoading(true);
    try {
      const res = await axios.post(`${API}/chat/message`, { session_id: sessionId, message: msgText });
      const aiText = res.data.message;
      setMessages(prev => [...prev, { role: "assistant", content: aiText, timestamp: new Date().toISOString() }]);
      lastSashaResponseRef.current = Date.now(); // reset stall timer on every Sasha response
      if (res.data.phase_complete != null) setPhaseComplete(res.data.phase_complete, sessionId); // track last phase transition
      if (res.data.progress) setProgress(res.data.progress);
      if (res.data.intake_ready) setIntakeReady(true);
      if (res.data.ui_card) setUiCard(res.data.ui_card); else setUiCard(null);
      // Phase 5 (v9.1): backend sends final_review_data once call pref + fee are captured
      if (res.data.final_review_data && !submitted) {
        setFinalReviewData(res.data.final_review_data);
        setEditingFinalReview(JSON.parse(JSON.stringify(res.data.final_review_data)));
      }
      // Post-quick-reply stall check: Sasha answered but didn't ask a follow-up → nudge in 1.5s
      // Skip if Sasha gave an open-ended invitation (doesn't need a "?" to be a real prompt)
      const isOpenInvite = /(go ahead|please share|feel free|tell me|share any|describe|explain|what happened|additional detail)/i.test(aiText);
      if (QUICK_REPLY_NUDGE_ENABLED && overrideText && !res.data.intake_ready && !aiText.includes('?') && !isOpenInvite) {
        const nudgeMsg = getStallNudge();
        stallTimerRef.current = setTimeout(async () => {
          if (uiModeRef.current !== "chatting" || intakeReadyRef.current || !sessionIdRef.current) return;
          loadingRef.current = true; setLoading(true);
          try {
            const nudge = await axios.post(`${API}/chat/message`, {
              session_id: sessionIdRef.current,
              message: nudgeMsg
            });
            lastSashaResponseRef.current = Date.now();
            stallNudgeCountRef.current += 1;
            if (nudge.data.phase_complete != null) setPhaseComplete(nudge.data.phase_complete, sessionIdRef.current);
            setMessages(prev => [...prev, { role: "assistant", content: nudge.data.message, timestamp: new Date().toISOString() }]);
            if (nudge.data.progress) setProgress(nudge.data.progress);
            if (nudge.data.intake_ready) { intakeReadyRef.current = true; setIntakeReady(true); }
            if (nudge.data.ui_card) setUiCard(nudge.data.ui_card); else setUiCard(null);
            if (nudge.data.case_summary_data && !caseSummaryConfirmed) {
              uiModeRef.current = "card:summary";
              setUiMode("card:summary");
              setCaseSummaryData(nudge.data.case_summary_data);
              setEditingCaseSummary({ ...nudge.data.case_summary_data });
            }
          } catch (e) {
          } finally {
            loadingRef.current = false; setLoading(false);
          }
        }, 1500);
      }
      // Handle contact confirmation card
      if (res.data.contact_data && !contactConfirmed) {
        uiModeRef.current = "card:contact"; // synchronous — timers blocked before React re-renders
        if (stallTimerRef.current) { clearTimeout(stallTimerRef.current); stallTimerRef.current = null; }
        setUiMode("card:contact");
        setContactData(res.data.contact_data);
        setEditingContact({ ...res.data.contact_data, phone: formatPhone(res.data.contact_data.phone) });
      }
      // Handle case summary card
      if (res.data.case_summary_data && !caseSummaryConfirmed) {
        uiModeRef.current = "card:summary"; // synchronous — timers blocked before React re-renders
        if (stallTimerRef.current) { clearTimeout(stallTimerRef.current); stallTimerRef.current = null; }
        setUiMode("card:summary");
        setCaseSummaryData(res.data.case_summary_data);
        setEditingCaseSummary({ ...res.data.case_summary_data });
      }
      // Handle summary card
      if (res.data.summary_data) {
        setSummaryData(res.data.summary_data);
        setEditingSummary(normalizeSummary(res.data.summary_data));
      }
      // Store user confirmation status from AI response
      if (res.data.user_confirmed) {
        sessionStorage.setItem(`userConfirmed_${sessionId}`, res.data.user_confirmed);
      }
    } catch {
      // Silent fail — no hardcoded messages
    } finally { setLoading(false); inputRef.current?.focus(); }
  };

  // ── Microphone permission request ────────────────────────────
  const requestMicPermission = async () => {
    if (!navigator.mediaDevices?.getUserMedia) {
      setMicPermission("unavailable");
      return false;
    }
    setMicPermission("requesting");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach(t => t.stop()); // Stop immediately — we just needed permission
      setMicPermission("granted");
      return true;
    } catch (err) {
      const isDenied = err.name === "NotAllowedError" || err.name === "PermissionDeniedError";
      setMicPermission(isDenied ? "denied" : "unavailable");
      if (isDenied) {
        setError("Microphone access was denied. Please click the 🔒 lock icon in your browser address bar → Site settings → Allow Microphone, then refresh.");
      } else {
        setError("Microphone not available. Your browser or device may not support audio recording.");
      }
      return false;
    }
  };

  // ── Live Voice via WebRTC (OpenAI Realtime) ──────────────────
  const startLiveVoice = async () => {
    if (liveVoice) { stopLiveVoice(); return; }
    if (mediaRecorderRef.current) stopDictation(); // free the mic from dictation before the live call
    setError(null);
    setLiveStatus("connecting");
    setLiveVoice(true);
    // Hard duration cap: auto-end the call after MAX_CALL_MS so a forgotten open call can't run up the bill.
    if (callTimerRef.current) clearTimeout(callTimerRef.current);
    callTimerRef.current = setTimeout(() => {
      stopLiveVoice();
      setError("Voice call ended (time limit). You can keep going by typing — or start a new call.");
    }, MAX_CALL_MS);
    voiceMessagesRef.current = [];
    // Capture where the chat currently is so voice picks up EXACTLY there.
    const convo = messages.filter(m => !m.isConfirmation && (m.role === "assistant" || m.role === "user"));
    const lastMsg = convo[convo.length - 1];
    const lastAssistantLine = [...convo].reverse().find(m => m.role === "assistant")?.content || "";

    try {
      // Fetch current VAD settings from backend
      let vadSettings = { threshold: 0.99, prefix_padding_ms: 500, silence_duration_ms: 2500 };
      try {
        const vadRes = await axios.get(`${API}/voice/settings`);
        vadSettings = vadRes.data;
      } catch {}

      // 1. Get ephemeral session from backend
      const sessionRes = await axios.post(`${API}/realtime/session?session_id=${sessionIdRef.current}`);
      const ephemeral = sessionRes.data;
      if (!ephemeral.client_secret) throw new Error("Failed to create voice session");

      // 2. Create WebRTC peer connection
      const pc = new RTCPeerConnection();
      pcRef.current = pc;

      pc.onconnectionstatechange = () => {
        if (pc.connectionState === "failed" || pc.connectionState === "disconnected") {
          setError("Voice connection lost. Reconnecting...");
          stopLiveVoice();
        }
      };

      // 3. Set up remote audio playback
      const audioEl = document.createElement("audio");
      audioEl.autoplay = true;
      audioElRef.current = audioEl;
      pc.ontrack = (e) => { audioEl.srcObject = e.streams[0]; };

      // 4. Get mic and add track
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true }
      });
      liveStreamRef.current = stream;
      setMicPermission("granted");
      stream.getTracks().forEach(track => pc.addTrack(track, stream));

      // 5. Set up data channel for events
      const dc = pc.createDataChannel("oai-events");
      dcRef.current = dc;

      dc.onopen = async () => {
        // The full Sasha script is ALREADY baked into the session token (server-side), so we
        // do NOT resend instructions here (resending could clear/override them). We only set
        // the audio config, in the GA Realtime shape (audio.input / audio.output).
        const configMsg = {
          type: "session.update",
          session: {
            type: "realtime",
            output_modalities: ["audio"],
            audio: {
              input: {
                transcription: { model: "whisper-1", language: "en" },
                turn_detection: { type: "server_vad", threshold: vadSettings.threshold, prefix_padding_ms: vadSettings.prefix_padding_ms, silence_duration_ms: vadSettings.silence_duration_ms },
              },
              output: { voice: vadSettings.voice || "nova" },
            },
          }
        };
        dc.send(JSON.stringify(configMsg));

        // Sasha picks up EXACTLY where the chat is. The FULL Sasha script — including the exact
        // OPENING MESSAGE and the conversation-so-far + resume signal — is already baked into the
        // session instructions server-side (build_voice_instructions). So for the opening turn we
        // let that baked script drive and do NOT override `response.instructions`: in the Realtime
        // API a per-response `instructions` REPLACES the session script for that turn, which is
        // exactly what was making Sasha improvise a generic "how are you / what's on your mind"
        // greeting instead of following the script.
        setTimeout(() => {
          let responsePayload;
          if (!lastMsg) {
            // Fresh call → let the baked script deliver the scripted OPENING MESSAGE verbatim.
            responsePayload = { type: "response.create" };
          } else if (lastMsg.role === "user") {
            // Client already typed an answer → the baked history + resume signal make Sasha
            // acknowledge it and continue on-script. No per-turn override.
            responsePayload = { type: "response.create" };
          } else {
            // Last on-screen line is Sasha's → have her voice that exact line so the client hears
            // the pending question, then continue. This single verbatim read is the only case we
            // steer per-turn; every subsequent turn runs on the baked script.
            responsePayload = {
              type: "response.create",
              response: {
                instructions: `Speak in ENGLISH. Say this out loud to the client, verbatim — this is exactly where the conversation is right now:\n\n"${lastAssistantLine}"\n\nThen wait for their reply and continue the intake from here, following your script exactly. Do NOT restart, do NOT re-introduce yourself, and do NOT add generic small talk like "how are you" or "what's on your mind".`
              }
            };
          }
          dc.send(JSON.stringify(responsePayload));
          setLiveStatus("speaking");
        }, 300);
      };

      dc.onmessage = (event) => {
        try {
          const msg = JSON.parse(event.data);
          handleRealtimeEvent(msg);
        } catch {}
      };

      // 6. Create SDP offer and negotiate
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);

      const negRes = await axios.post(`${API}/realtime/negotiate`, offer.sdp, {
        headers: { "Content-Type": "application/sdp" }
      });
      const answer = { type: "answer", sdp: negRes.data.sdp };
      await pc.setRemoteDescription(answer);

    } catch (err) {
      console.error("Live voice init error:", err);
      const msg = err.response?.data?.detail || err.message || "Could not start voice session";
      setError(msg);
      setLiveVoice(false);
      setLiveStatus("idle");
      cleanupWebRTC();
    }
  };

  // Append a user voice line to the visible transcript AND the backend-sync buffer. Centralized so
  // every source of a user transcript renders identically — the single write point for "my words".
  const addUserVoiceLine = (text) => {
    const clean = (text || "").trim();
    if (!clean) return;
    const userMsg = { role: "user", content: clean, timestamp: new Date().toISOString(), isVoice: true };
    setMessages(prev => [...prev, userMsg]);
    voiceMessagesRef.current.push(userMsg);
    setMsgCount(c => c + 1);
    setLiveStatus("thinking");
  };

  const handleRealtimeEvent = (msg) => {
    const type = msg.type;
    // Cascading fallback: OpenAI has used a few names/shapes for the user-input transcript across
    // beta→GA. Accept all known completed variants so a single rename can't silently hide "my words".
    if (
      type === "conversation.item.input_audio_transcription.completed" ||
      type === "conversation.item.input_audio_transcription.done"
    ) {
      addUserVoiceLine(msg.transcript);
    } else if (type === "conversation.item.input_audio_transcription.failed") {
      // Real production alert (NOT a debug log): transcription was on but failed, so the
      // user's words can never appear. Surface it instead of failing silently.
      console.error("[CRITICAL] user audio transcription FAILED — spoken words will not show in chat:", JSON.stringify(msg.error || msg));
    } else if (type === "response.audio_transcript.done") {
      const text = msg.transcript?.trim();
      if (text) {
        const aiMsg = { role: "assistant", content: text, timestamp: new Date().toISOString() };
        setMessages(prev => [...prev, aiMsg]);
        voiceMessagesRef.current.push(aiMsg);
        // Check if AI signaled intake is complete — tag detection + smart phrase matching
        const lower = text.toLowerCase();
        const hasTag = text.includes("[INTAKE_READY]") || text.includes("[SUBMISSION_READY]") || text.includes("[USER_WRAPPING]");
        const hasPhrase = ["submit button", "hit submit", "send your intake", "ready to submit", "is everything correct", "everything correct", "look it over", "on file", "review and submit"].some(p => lower.includes(p));
        const hasSummary = (lower.includes("contact information") || lower.includes("case information") || lower.includes("incident location")) && (lower.includes("name") || lower.includes("phone") || lower.includes("email"));
        if (hasTag || hasPhrase || hasSummary) {
          setIntakeReady(true);
        }
      }
    } else if (type === "response.audio.delta") {
      setLiveStatus("speaking");
    } else if (type === "response.done") {
      if (interruptTimerRef.current) { clearTimeout(interruptTimerRef.current); interruptTimerRef.current = null; }
      setLiveStatus("listening");
      // After each complete AI response, sync messages to backend to check for cards
      const vmsg = voiceMessagesRef.current;
      if (vmsg.length >= 1 && sessionIdRef.current) {
        axios.post(`${API}/chat/sync-voice`, { session_id: sessionIdRef.current, messages: vmsg })
          .then(res => {
            if (res.data.progress) setProgress(res.data.progress);
            if (res.data.intake_ready) setIntakeReady(true);
            if (res.data.ui_card) setUiCard(res.data.ui_card); else setUiCard(null);
            if (res.data.contact_data && !contactConfirmed) {
              setContactData(res.data.contact_data);
              setEditingContact({ ...res.data.contact_data });
            }
            if (res.data.case_summary_data && !caseSummaryConfirmed) {
              setCaseSummaryData(res.data.case_summary_data);
              setEditingCaseSummary({ ...res.data.case_summary_data });
            }
            if (res.data.final_review_data && !submitted) {
              setFinalReviewData(res.data.final_review_data);
              setEditingFinalReview(JSON.parse(JSON.stringify(res.data.final_review_data)));
            }
            if (res.data.summary_data) {
              setSummaryData(res.data.summary_data);
              setEditingSummary(normalizeSummary(res.data.summary_data));
            }
            // Clear synced messages so we don't re-sync duplicates
            voiceMessagesRef.current = [];
          })
          .catch(() => {});
      }
    } else if (type === "input_audio_buffer.speech_started") {
      // Noise gate: only interrupt Sasha if user speaks for >350ms continuously.
      // Short blips (coughs, background sounds, <350ms) clear the timer and Sasha keeps talking.
      setLiveStatus("listening");
      if (interruptTimerRef.current) clearTimeout(interruptTimerRef.current);
      interruptTimerRef.current = setTimeout(() => {
        interruptTimerRef.current = null;
        const dc = dcRef.current;
        if (dc && dc.readyState === "open") {
          try { dc.send(JSON.stringify({ type: "response.cancel" })); } catch (e) {
          }
        }
      }, 350);
    } else if (type === "input_audio_buffer.speech_stopped") {
      // User stopped speaking before 350ms — was noise, clear the timer
      if (interruptTimerRef.current) {
        clearTimeout(interruptTimerRef.current);
        interruptTimerRef.current = null;
      }
    } else if (type === "error") {
    }
  };

  const cleanupWebRTC = () => {
    if (interruptTimerRef.current) { clearTimeout(interruptTimerRef.current); interruptTimerRef.current = null; }
    if (dcRef.current) { try { dcRef.current.close(); } catch {} dcRef.current = null; }
    if (pcRef.current) { try { pcRef.current.close(); } catch {} pcRef.current = null; }
    if (audioElRef.current) { audioElRef.current.srcObject = null; audioElRef.current = null; }
    if (liveStreamRef.current) { liveStreamRef.current.getTracks().forEach(t => t.stop()); liveStreamRef.current = null; }
  };

  // ── Dictation: speak → text in the input box (review, then send) ──────────────
  // This is NOT the live call. It records a short clip, sends it to /chat/transcribe for OpenAI
  // transcription, and drops the English text into the textarea so the user can edit/send it.
  const startDictation = async () => {
    if (liveVoice) return; // the live call owns the mic — don't fight it
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true } });
      dictationStreamRef.current = stream;
      setMicPermission("granted");
      // Anchor the live preview + Whisper text to whatever was already in the box.
      dictationBaseRef.current = (inputRef.current?.value || "").trim();
      liveTranscriptRef.current = "";
      webSpeechActiveRef.current = false;
      const mr = new MediaRecorder(stream);
      mediaRecorderRef.current = mr;
      dictationChunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data && e.data.size > 0) dictationChunksRef.current.push(e.data); };
      mr.onstop = async () => {
        // release the mic right away
        try { dictationStreamRef.current?.getTracks().forEach(t => t.stop()); } catch {}
        dictationStreamRef.current = null;
        const blob = new Blob(dictationChunksRef.current, { type: mr.mimeType || "audio/webm" });
        dictationChunksRef.current = [];
        // If the user hit Send mid-dictation, we transcribe then auto-send the result.
        const autoSend = sendAfterTranscribeRef.current;
        sendAfterTranscribeRef.current = false;
        // Hybrid: when Web Speech drove the live preview, the box already shows interim words — start from the
        // pre-dictation BASE so Whisper's accurate text REPLACES the preview (no duplication). Otherwise read the box.
        const usedWebSpeech = webSpeechActiveRef.current;
        const existing = usedWebSpeech ? (dictationBaseRef.current || "") : (inputRef.current?.value || "").trim();
        const liveFallback = (liveTranscriptRef.current || "").trim(); // Web Speech text — used only if Whisper fails/empty
        const joinWith = (a, b) => (a ? (b ? a + " " + b : a) : b); // safe join, no stray spaces
        if (!blob.size) {
          // No audio captured — fall back to whatever Web Speech heard so the user never loses their words.
          setTranscribing(false);
          const combined = joinWith(existing, liveFallback);
          if (autoSend) { if (combined) { setInput(""); sendMessage(combined); } }
          else if (liveFallback) { setInput(combined); }
          return;
        }
        setTranscribing(true);
        try {
          const fd = new FormData();
          fd.append("audio", blob, "dictation.webm");
          const res = await axios.post(`${API}/chat/transcribe`, fd, { headers: { "Content-Type": "multipart/form-data" } });
          const whisper = (res.data?.text || "").trim();
          const finalText = whisper || liveFallback; // Whisper is the source of truth; fall back to the live transcript if empty
          const combined = joinWith(existing, finalText);
          if (autoSend) {
            if (combined) { setInput(""); sendMessage(combined); }
            else setError("Didn't catch that — try again.");
          } else if (finalText) {
            setInput(combined);
            requestAnimationFrame(() => {
              const el = inputRef.current;
              if (el) { el.focus(); el.style.height = 'auto'; el.style.height = Math.min(el.scrollHeight, 100) + 'px'; }
            });
          } else {
            setError("Didn't catch that — try dictating again.");
          }
        } catch (err) {
          if (liveFallback) {
            // Whisper request failed but Web Speech gave us text — use it so the user keeps their words.
            const combined = joinWith(existing, liveFallback);
            if (autoSend) { setInput(""); sendMessage(combined); }
            else { setInput(combined); requestAnimationFrame(() => { const el = inputRef.current; if (el) { el.focus(); el.style.height = 'auto'; el.style.height = Math.min(el.scrollHeight, 100) + 'px'; } }); }
          } else {
            setError(err.response?.data?.detail || "Could not transcribe audio. Please try again.");
          }
        } finally {
          setTranscribing(false);
        }
      };
      mr.start();
      setDictating(true);

      // ── Hybrid live preview: stream words into the box as the user speaks (free, browser-native) ──
      // Whisper (mr.onstop) remains the source of truth; this is the live "typing as you talk" layer only.
      const SR = typeof window !== "undefined" && (window.SpeechRecognition || window.webkitSpeechRecognition);
      if (SR) {
        try {
          const rec = new SR();
          rec.lang = "en-US";        // English lock — matches the rest of the intake
          rec.continuous = true;      // keep listening across natural pauses
          rec.interimResults = true;  // emit words as they're recognized, not just at the end
          stoppingDictationRef.current = false;
          rec.onresult = (e) => {
            let finalT = "", interimT = "";
            for (let i = e.resultIndex; i < e.results.length; i++) {
              const seg = e.results[i][0]?.transcript || "";
              if (e.results[i].isFinal) finalT += seg; else interimT += seg;
            }
            if (finalT) liveTranscriptRef.current = (liveTranscriptRef.current + " " + finalT).replace(/\s+/g, " ").trim();
            const live = (liveTranscriptRef.current + " " + interimT).replace(/\s+/g, " ").trim();
            const base = dictationBaseRef.current;
            const combined = base ? (live ? base + " " + live : base) : live;
            setInput(combined);
            requestAnimationFrame(() => {
              const el = inputRef.current;
              if (el) { el.style.height = 'auto'; el.style.height = Math.min(el.scrollHeight, 100) + 'px'; }
            });
          };
          rec.onerror = () => {}; // swallow recognizer errors — Whisper still finalizes the dictation
          rec.onend = () => {
            // Browsers auto-stop the recognizer on silence; if the user is still dictating, restart to keep the live feed alive.
            if (!stoppingDictationRef.current && mediaRecorderRef.current) {
              try { rec.start(); } catch {} // keep the live feed alive across browser silence auto-stops
            }
          };
          recognitionRef.current = rec;
          webSpeechActiveRef.current = true;
          rec.start();
        } catch (err) {
          recognitionRef.current = null;
          webSpeechActiveRef.current = false;
        }
      } else {
        webSpeechActiveRef.current = false;
      }
    } catch (err) {
      console.error("Dictation mic error:", err);
      if (err.name === "NotAllowedError" || err.name === "NotFoundError") {
        setMicPermission("denied");
        setError("Microphone blocked. Allow mic access to use dictation.");
      } else {
        setError("Could not start dictation. Please try again.");
      }
      setDictating(false);
    }
  };

  const stopDictation = () => {
    const mr = mediaRecorderRef.current;
    setDictating(false);
    // Stop the live recognizer first and flag it so onend doesn't auto-restart after an intentional stop.
    stoppingDictationRef.current = true;
    if (recognitionRef.current) {
      try { recognitionRef.current.stop(); } catch {}
      recognitionRef.current = null;
    }
    if (mr && mr.state !== "inactive") { try { mr.stop(); } catch {} }
    mediaRecorderRef.current = null;
  };

  const toggleDictation = () => { if (dictating) stopDictation(); else startDictation(); };

  // Tap-confirm → voice feedback: when the user taps/selects a card during a live
  // voice call, inject it into the realtime session so Sasha acknowledges and moves
  // on without the user also having to say it out loud.
  const injectVoiceConfirmation = (text) => {
    const dc = dcRef.current;
    if (!liveVoice || !dc || dc.readyState !== "open") return false;
    try {
      dc.send(JSON.stringify({
        type: "conversation.item.create",
        item: { type: "message", role: "user", content: [{ type: "input_text", text }] },
      }));
      dc.send(JSON.stringify({ type: "response.create" }));
      // Mirror it into the transcript so the backend sync captures the value too.
      voiceMessagesRef.current.push({ role: "user", content: text, timestamp: new Date().toISOString(), isVoice: true });
      return true;
    } catch { return false; }
  };

  // Flush all pending voice messages to backend before mode switch — prevents context loss
  const flushVoiceMessages = async () => {
    const sid = sessionIdRef.current;
    const vmsg = voiceMessagesRef.current;
    if (!sid || !vmsg.length) return;
    try {
      const res = await axios.post(`${API}/chat/sync-voice`, { session_id: sid, messages: vmsg });
      if (res.data.progress) setProgress(res.data.progress);
      if (res.data.intake_ready) setIntakeReady(true);
      if (res.data.ui_card) setUiCard(res.data.ui_card); else setUiCard(null);
      if (res.data.contact_data && !contactConfirmed) {
        setContactData(res.data.contact_data);
        setEditingContact({ ...res.data.contact_data, phone: formatPhone(res.data.contact_data.phone) });
      }
      if (res.data.case_summary_data && !caseSummaryConfirmed) {
        setCaseSummaryData(res.data.case_summary_data);
        setEditingCaseSummary({ ...res.data.case_summary_data });
      }
      if (res.data.final_review_data && !submitted) {
        setFinalReviewData(res.data.final_review_data);
        setEditingFinalReview(JSON.parse(JSON.stringify(res.data.final_review_data)));
      }
      if (res.data.summary_data) {
        setSummaryData(res.data.summary_data);
        setEditingSummary(normalizeSummary(res.data.summary_data));
      }
      voiceMessagesRef.current = [];
    } catch (e) {
    }
  };

  const stopLiveVoice = async () => {
    // Clear the duration cap so it never fires after a normal hangup.
    if (callTimerRef.current) { clearTimeout(callTimerRef.current); callTimerRef.current = null; }
    cleanupWebRTC();
    setLiveVoice(false);
    setLiveStatus("idle");
    const sid = sessionIdRef.current;
    if (!sid) return;
    // Await flush so text mode always has full context
    const vmsg = voiceMessagesRef.current;
    if (vmsg.length > 0) {
      try {
        const res = await axios.post(`${API}/chat/sync-voice`, { session_id: sid, messages: vmsg });
        if (res.data.progress) setProgress(res.data.progress);
        if (res.data.intake_ready) setIntakeReady(true);
        if (res.data.ui_card) setUiCard(res.data.ui_card); else setUiCard(null);
        if (res.data.contact_data && !contactConfirmed) {
          setContactData(res.data.contact_data);
          setEditingContact({ ...res.data.contact_data });
        }
        if (res.data.case_summary_data && !caseSummaryConfirmed) {
          setCaseSummaryData(res.data.case_summary_data);
          setEditingCaseSummary({ ...res.data.case_summary_data });
        }
        if (res.data.summary_data) {
          setSummaryData(res.data.summary_data);
          setEditingSummary(normalizeSummary(res.data.summary_data));
        }
        if (res.data.user_confirmed) {
          sessionStorage.setItem(`userConfirmed_${sid}`, res.data.user_confirmed);
        }
      } catch (e) { console.error("Voice sync error:", e); }
      voiceMessagesRef.current = [];
    }
    // Always reload session to get full conversation + re-check cards
    try {
      const sessionRes = await axios.get(`${API}/chat/${sid}`);
      if (sessionRes.data.messages) {
        const allMsgs = sessionRes.data.messages.map(m => ({ role: m.role, content: m.content, timestamp: m.timestamp }));
        setMessages(allMsgs);
        setMsgCount(allMsgs.filter(m => m.role === "user").length);
      }
      if (sessionRes.data.progress) setProgress(sessionRes.data.progress);
      if (sessionRes.data.contact_confirmed) setContactConfirmed(true);
    } catch (e) { console.error("Session reload error:", e); }
  };

  // Cleanup on unmount
  useEffect(() => () => { cleanupWebRTC(); }, []);

  // ── Edit handler for summary overlay — sends correction request to Sasha ──

  const completeIntake = async () => {
    if (!sessionId || completing) return;
    setCompleting(true);
    clearInterval(heartbeatRef.current);
    if (currentAudioRef.current) currentAudioRef.current.pause();
    stopLiveVoice();
    try {
      const userConfirmed = sessionStorage.getItem(`userConfirmed_${sessionId}`) || "Auto-submitted";
      const res = await axios.post(`${API}/chat/${sessionId}/complete?user_confirmed=${encodeURIComponent(userConfirmed)}`, editingSummary);
      localStorage.removeItem("intakeSessionId");
      sessionStorage.removeItem(`userConfirmed_${sessionId}`);
      navigate("/intake/success", { state: { leadData: res.data.lead_data, leadId: res.data.lead_id, sessionId: sessionId } });
    } catch { setError("Unable to finalize. Please try again."); setCompleting(false); }
  };

  const handleOverlayConfirm = async (contact) => {
    setShowContactOverlay(false);
    setEditingContact(contact);
    if (sessionId) {
      try {
        await axios.post(`${API}/chat/${sessionId}/confirm-contact`, contact);
        setContactConfirmed(true);
        setContactData(null);
      } catch (e) {
      }
    } else {
      // Session not ready yet — store and confirm once session is available
      pendingOverlayContact.current = contact;
    }
  };

  const confirmContactInfo = async () => {
    if (!sessionId) return;
    setLoading(true);
    setError(null);
    setUiCard(null);
    try {
      const res = await axios.post(`${API}/chat/${sessionId}/confirm-contact`, editingContact);
      const wasAlreadyConfirmed = editingConfirmedContact;
      setContactConfirmed(true);
      setEditingConfirmedContact(false);
      uiModeRef.current = "chatting";
      setUiMode("chatting");
      // Voice: tell Sasha it's confirmed so she continues by voice (don't inject the text line).
      if (liveVoice) {
        injectVoiceConfirmation(`I've confirmed my contact info on the screen — name ${editingContact.name}, that's all correct.`);
      } else if (!wasAlreadyConfirmed && res.data.next_message) {
        lastSashaResponseRef.current = Date.now();
        setMessages(prev => [...prev, { role: "assistant", content: res.data.next_message, timestamp: new Date().toISOString() }]);
        if (res.data.ui_card) setUiCard(res.data.ui_card);
      }
    } catch (e) {
      setError("Unable to save contact info. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const confirmCaseSummary = async () => {
    if (!sessionId) return;
    setLoading(true);
    setError(null);
    setUiCard(null);
    try {
      const res = await axios.post(`${API}/chat/${sessionId}/confirm-case-summary`, editingCaseSummary);
      setCaseSummaryConfirmed(true);
      // keep caseSummaryData — card stays visible as "Confirmed" in the conversation
      uiModeRef.current = "chatting";
      setUiMode("chatting");
      setMessages(prev => [...prev, {
        role: "system",
        content: "Case summary confirmed",
        timestamp: new Date().toISOString(),
        isConfirmation: true
      }]);
      // Voice: tell Sasha it's confirmed so she asks the next question by voice.
      if (liveVoice) {
        injectVoiceConfirmation("I've reviewed and confirmed my case summary on the screen — it looks right.");
      } else if (res.data.next_message) {
        lastSashaResponseRef.current = Date.now();
        setMessages(prev => [...prev, { role: "assistant", content: res.data.next_message, timestamp: new Date().toISOString() }]);
        if (res.data.ui_card) setUiCard(res.data.ui_card);
      }
    } catch (e) {
      setError("Unable to save case summary. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const startOver = async () => {
    if (!sessionId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await axios.post(`${API}/chat/${sessionId}/start-over`);
      setCaseSummaryData(null);
      setCaseSummaryConfirmed(false);
      setEditingCaseSummary({});
      setCaseSummaryEditMode(false);
      setShowStartOverConfirm(false);
      setUiCard(null);
      uiModeRef.current = "chatting";
      setUiMode("chatting");
      if (res.data.next_message) {
        lastSashaResponseRef.current = Date.now();
        setMessages(prev => [...prev, { role: "assistant", content: res.data.next_message, timestamp: new Date().toISOString() }]);
      }
    } catch (e) {
      setError("Unable to restart. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const isMobile = typeof window !== 'undefined' && /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      if (isMobile) {
        // Mobile: Enter = new line, send via button only
        return;
      }
      // Desktop: Enter = send, Shift+Enter = new line
      if (!e.shiftKey) { e.preventDefault(); sendMessage(); }
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !sessionId) return;
    if (file.size > 20 * 1024 * 1024) { setError("File too large. Max 20MB."); return; }
    setUploading(true); setError(null);
    try {
      const formData = new FormData();
      formData.append("session_id", sessionId);
      formData.append("file", file);
      const res = await axios.post(`${API}/chat/upload`, formData, { headers: { "Content-Type": "multipart/form-data" } });
      setMessages(prev => [...prev, { role: "user", content: `📎 Uploaded: ${file.name}`, timestamp: new Date().toISOString(), isFile: true }]);
      const aiRes = await axios.post(`${API}/chat/message`, { session_id: sessionId, message: `I've uploaded a document: ${file.name}` });
      setMessages(prev => [...prev, { role: "assistant", content: aiRes.data.message, timestamp: new Date().toISOString() }]);
      if (aiRes.data.intake_ready) setIntakeReady(true);
    } catch (err) { setError(err.response?.data?.detail || "Upload failed."); }
    finally { setUploading(false); if (fileInputRef.current) fileInputRef.current.value = ""; }
  };

  const formatTime = (ts) => {
    if (!ts) return "";
    try { return new Date(ts).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }); } catch { return ""; }
  };

  if (starting) return (
    <div className="fixed inset-0 flex items-center justify-center bg-background" style={{ height: 'var(--app-height, 100dvh)' }} data-testid="chat-loading">
      <div className="text-center space-y-4">
        <div className="w-14 h-14 rounded-2xl bg-primary/10 border border-primary/20 flex items-center justify-center mx-auto animate-pulse">
          <Sparkles className="w-7 h-7 text-primary" />
        </div>
        <p className="text-muted-foreground text-sm">Connecting to Sasha...</p>
      </div>
    </div>
  );

  const micBlocked = micPermission === "denied";
  const micUnavailable = micPermission === "unavailable";

  // ── Talk vs Type entry screen ──────────────────────────────
  if (!starting && modeChoice === null && messages.length === 0) {
    return (
      <div className="fixed inset-0 bg-background flex flex-col items-center justify-center px-6 overflow-hidden" style={{ height: 'var(--app-height, 100dvh)' }}>
        {/* Ambient glow — adds real depth. Calm, but actually visible. */}
        <div className="chat-ambient pointer-events-none absolute inset-0" aria-hidden="true" />
        <div className="relative z-10 w-full max-w-sm space-y-6 text-center animate-fade-in-up">
          {/* Sasha avatar */}
          <div className="flex flex-col items-center gap-3">
            <div className="relative w-16 h-16 rounded-full bg-gradient-to-br from-primary/25 to-primary/5 border border-primary/40 ring-4 ring-primary/5 flex items-center justify-center shadow-lg shadow-primary/20">
              <span className="text-2xl font-bold text-primary">S</span>
            </div>
            <div>
              <p className="text-lg font-bold">Hi, I'm Sasha</p>
              <p className="text-sm text-muted-foreground mt-1">Your legal intake specialist. How would you like to connect?</p>
            </div>
          </div>

          {/* Mode buttons */}
          <div className="space-y-3">
            {/* Type to Sasha — PRIMARY, recommended, bulletproof default (text + tap-cards + dictation). */}
            <button
              onClick={() => setModeChoice("text")}
              className="w-full flex items-center gap-4 p-4 rounded-2xl border-2 border-primary/40 bg-primary/10 hover:bg-primary/20 transition-all group card-glow shadow-sm"
            >
              <div className="w-12 h-12 rounded-full bg-primary/20 border border-primary/30 flex items-center justify-center flex-shrink-0">
                <Send className="w-5 h-5 text-primary" />
              </div>
              <div className="text-left">
                <p className="font-semibold text-sm">Type to Sasha</p>
                <p className="text-xs text-muted-foreground mt-0.5">Type or tap — or tap the 🎙️ mic and just <strong className="font-semibold text-foreground/80">speak your legal situation</strong> out loud. Works on every device.</p>
              </div>
              <span className="ml-auto text-xs bg-primary/20 text-primary px-2 py-1 rounded-full font-medium">Recommended</span>
            </button>

            {/* Live voice — gated Beta. Renders ONLY when VOICE_CALL_ENABLED. Three states: unknown (checking), granted (ready), prompt (will ask). */}
            {VOICE_CALL_ENABLED && micPermission === "unknown" && (
              <div className="w-full flex items-center gap-4 p-4 rounded-2xl border-2 border-border/40 bg-muted/10 animate-pulse cursor-wait" aria-hidden="true">
                <div className="w-12 h-12 rounded-full bg-muted/40 border border-border/30 flex-shrink-0" />
                <div className="text-left space-y-1.5">
                  <div className="h-3.5 w-28 rounded bg-muted/60" />
                  <div className="h-3 w-40 rounded bg-muted/40" />
                </div>
              </div>
            )}
            {VOICE_CALL_ENABLED && micPermission === "granted" && (
              <button
                onClick={async () => { setModeChoice("voice"); await startLiveVoice(); }}
                className="w-full flex items-center gap-4 p-4 rounded-2xl border border-border bg-card hover:border-primary/30 hover:bg-muted/40 transition-all group"
              >
                <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center flex-shrink-0 border border-border">
                  <Mic className="w-5 h-5 text-muted-foreground" />
                </div>
                <div className="text-left">
                  <p className="font-semibold text-sm">Talk to Sasha</p>
                  <p className="text-xs text-muted-foreground mt-0.5">Beta — talk like a phone call. If it's glitchy, switch to typing anytime.</p>
                </div>
                <span className="ml-auto text-xs bg-muted text-muted-foreground px-2 py-1 rounded-full font-medium">Beta</span>
              </button>
            )}
            {VOICE_CALL_ENABLED && (micPermission === "prompt" || (!micBlocked && !micUnavailable && micPermission !== "granted" && micPermission !== "unknown")) && (
              <button
                onClick={async () => { setModeChoice("voice"); await startLiveVoice(); }}
                className="w-full flex items-center gap-4 p-4 rounded-2xl border border-border bg-card hover:border-primary/30 hover:bg-muted/40 transition-all group"
              >
                <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center flex-shrink-0 border border-border">
                  <Mic className="w-5 h-5 text-muted-foreground" />
                </div>
                <div className="text-left">
                  <p className="font-semibold text-sm">Talk to Sasha</p>
                  <p className="text-xs text-muted-foreground mt-0.5">Beta — allow microphone when prompted. Switch to typing anytime.</p>
                </div>
                <span className="ml-auto text-xs bg-muted text-muted-foreground px-2 py-1 rounded-full font-medium">Beta</span>
              </button>
            )}

            {VOICE_CALL_ENABLED && micBlocked && (
              <p className="text-xs text-muted-foreground bg-muted/30 rounded-lg p-3">
                Microphone blocked. To enable voice, click the lock icon in your browser and allow microphone access.
              </p>
            )}

            {/* Live voice OFF (launch default) — show as a disabled "Coming Soon" card so users know it's planned, not broken. Code stays intact; flip VOICE_CALL_ENABLED to bring it back as Beta. */}
            {!VOICE_CALL_ENABLED && (
              <button
                type="button"
                disabled
                aria-disabled="true"
                title="Live voice call is coming soon"
                className="w-full flex items-center gap-4 p-4 rounded-2xl border border-border bg-muted/30 opacity-60 cursor-not-allowed"
                data-testid="talk-to-sasha-coming-soon"
              >
                <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center flex-shrink-0 border border-border">
                  <Mic className="w-5 h-5 text-muted-foreground" />
                </div>
                <div className="text-left">
                  <p className="font-semibold text-sm text-muted-foreground">Talk to Sasha</p>
                  <p className="text-xs text-muted-foreground/70 mt-0.5">Live voice call — coming soon. For now, type or tap the 🎙️ mic to speak your answers.</p>
                </div>
                <span className="ml-auto text-xs bg-muted text-muted-foreground px-2 py-1 rounded-full font-medium">Coming Soon</span>
              </button>
            )}
          </div>

          {/* Human fallback — visible everywhere. REPLACE the placeholders with the firm's real number/email before launch. */}
          <p className="text-xs text-muted-foreground text-center mt-3">
            Prefer a person? Call <a href="tel:+1XXXXXXXXXX" className="underline">(XXX) XXX-XXXX</a> or email{" "}
            <a href="mailto:REPLACE@firm.com" className="underline">REPLACE@firm.com</a>.
          </p>

          <p className="text-[11px] text-muted-foreground/60">Your information is encrypted and only shared with licensed attorneys.</p>
        </div>
      </div>
    );
  }

  // ── Case Summary Card — compact view mode, textareas only appear in edit mode ──
  // ── Shared case-analysis sections (v9.2) — attorney-intake formula, identical in both cards:
  //    Case Type → Background → Incident Summary → Key Facts → Potential Claims → Evidence → Desired Outcome ──
  const CaseAnalysisSections = ({ analysis, location }) => {
    const a = analysis || {};
    const Section = ({ title, items }) => (
      (Array.isArray(items) && items.length > 0) ? (
        <div>
          <div className="text-[11px] uppercase tracking-wide text-violet-300/80 font-semibold mb-1">{title}</div>
          <ul className="space-y-1 list-none">
            {items.map((it, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-foreground/90 leading-relaxed">
                <span className="text-violet-400 mt-0.5 flex-shrink-0">•</span><span>{it}</span>
              </li>
            ))}
          </ul>
        </div>
      ) : null
    );
    const loc = location || a.location;
    return (
      <div className="space-y-3">
        {loc && <div className="text-sm text-muted-foreground">{loc}</div>}

        {/* 1. Case Type (+ potential claims as chips) */}
        {(a.case_type || (Array.isArray(a.sub_types) && a.sub_types.length > 0)) && (
          <div>
            <div className="text-[11px] uppercase tracking-wide text-violet-300/80 font-semibold mb-1">Case Type</div>
            {a.case_type && <div className="text-sm font-semibold text-foreground">{a.case_type}</div>}
            {Array.isArray(a.sub_types) && a.sub_types.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-1">
                {a.sub_types.map((s, i) => (
                  <span key={i} className="text-xs text-violet-200 px-2 py-0.5 rounded-full bg-violet-500/10 border border-violet-500/20">{s}</span>
                ))}
              </div>
            )}
          </div>
        )}

        {/* 2. Background */}
        <Section title="Background" items={a.background} />

        {/* 3. Incident Summary */}
        {a.incident_summary && (
          <div>
            <div className="text-[11px] uppercase tracking-wide text-violet-300/80 font-semibold mb-1">Incident Summary</div>
            <p className="text-sm text-foreground/90 leading-relaxed">{a.incident_summary}</p>
          </div>
        )}

        {/* 4. Key Facts */}
        <Section title="Key Facts" items={a.key_facts} />

        {/* 5. Potential Claims */}
        <Section title="Potential Claims" items={a.potential_claims} />

        {/* 6. Evidence Available */}
        <Section title="Evidence Available" items={a.evidence} />

        {/* 7. Desired Outcome */}
        {a.desired_outcome && (
          <div>
            <div className="text-[11px] uppercase tracking-wide text-violet-300/80 font-semibold mb-1">Desired Outcome</div>
            <p className="text-sm text-foreground/90 leading-relaxed">{a.desired_outcome}</p>
          </div>
        )}
      </div>
    );
  };

  // ── Shared editor — every section pre-filled & editable, used by both cards.
  //    Lists edit as one-item-per-line textareas (compact, no tiny boxes). Edits persist as-is. ──
  const linesToList = (s) => (s || "").split("\n").map(x => x.trim()).filter(Boolean);
  const listToLines = (a) => (Array.isArray(a) ? a.join("\n") : (a || ""));
  const CaseAnalysisEditor = ({ analysis, location, onLocation, onChange }) => {
    const a = analysis || {};
    const set = (patch) => onChange({ ...a, ...patch });
    const fieldCls = "w-full px-3 py-2 rounded-lg bg-secondary/40 border border-border/50 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500/30";
    const Lbl = ({ children }) => <label className="text-[11px] uppercase tracking-wide text-violet-300/80 font-semibold mb-1 block">{children}</label>;
    // Auto-size to content so the card never has an inner scrollbar — fully extended & visible
    const autoRows = (text, min = 2) => Math.max(min, (String(text || "").split("\n").length));
    const wrapRows = (text, min = 2) => Math.max(min, Math.ceil(String(text || "").length / 48));
    const ListField = ({ title, k, min = 2 }) => {
      const val = listToLines(a[k]);
      return (
        <div>
          <Lbl>{title}</Lbl>
          <textarea value={val} onChange={e => set({ [k]: linesToList(e.target.value) })} rows={autoRows(val, min)}
            placeholder="One item per line" className={fieldCls + " resize-none leading-relaxed overflow-hidden"} />
        </div>
      );
    };
    return (
      <div className="space-y-3">
        <div><Lbl>Location</Lbl>
          <input type="text" value={location || ""} onChange={e => onLocation(e.target.value)} className={fieldCls} data-testid="case-summary-location-input" />
        </div>
        <div><Lbl>Case Type</Lbl>
          <input type="text" value={a.case_type || ""} onChange={e => set({ case_type: e.target.value })} className={fieldCls} />
        </div>
        <div><Lbl>Potential Claims (chips)</Lbl>
          <input type="text" value={listToLines(a.sub_types).replace(/\n/g, ", ")} onChange={e => set({ sub_types: e.target.value.split(",").map(x => x.trim()).filter(Boolean) })} placeholder="Comma separated" className={fieldCls} />
        </div>
        <ListField title="Background" k="background" />
        <div><Lbl>Incident Summary</Lbl>
          <textarea value={a.incident_summary || ""} onChange={e => set({ incident_summary: e.target.value })} rows={wrapRows(a.incident_summary, 3)} className={fieldCls + " resize-none leading-relaxed overflow-hidden"} data-testid="case-summary-incident-input" />
        </div>
        <ListField title="Key Facts" k="key_facts" min={3} />
        <ListField title="Potential Claims" k="potential_claims" />
        <ListField title="Evidence Available" k="evidence" />
        <div><Lbl>Desired Outcome</Lbl>
          <input type="text" value={a.desired_outcome || ""} onChange={e => set({ desired_outcome: e.target.value })} className={fieldCls} />
        </div>
        <p className="text-[11px] text-muted-foreground/60">Your edits are saved exactly as written.</p>
      </div>
    );
  };

  const saveCaseSummaryEdits = async () => {
    if (sessionId) {
      try {
        const res = await axios.post(`${API}/chat/${sessionId}/save-case-summary`, {
          location: editingCaseSummary.location,
          analysis: editingCaseSummary.analysis,
        });
        const next = { ...editingCaseSummary };
        if (res.data.analysis) { next.analysis = res.data.analysis; next.case_category = res.data.analysis.case_type || next.case_category; }
        setEditingCaseSummary(next);
        setCaseSummaryData(next);
      } catch (e) { console.error('[BUILD] save case summary failed', e.message); }
    }
    setCaseSummaryEditMode(false);
  };

  const CaseSummaryCard = () => (
    <div className="flex gap-2 sm:gap-3 animate-fade-in" data-testid="case-summary-confirmation-card">
      <div className={`w-7 h-7 sm:w-8 sm:h-8 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 border ${caseSummaryConfirmed ? "bg-violet-500/20 border-violet-500/30" : "bg-primary/20 border-primary/20"}`}>
        <span className={`text-xs font-bold ${caseSummaryConfirmed ? "text-violet-400" : "text-primary"}`}>S</span>
      </div>
      <div className="w-full max-w-md">
        <div className={`p-4 sm:p-5 rounded-2xl backdrop-blur-sm space-y-3 border ${caseSummaryConfirmed ? "border-violet-500/30 bg-card/80" : "border-violet-500/40 bg-card/80"}`}>

          {/* Header */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <FileText className="w-4 h-4 text-violet-400" />
              <h3 className="font-semibold text-sm text-foreground">
                {caseSummaryConfirmed ? "Your Case Summary" : "Does this match your situation?"}
              </h3>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              {caseSummaryConfirmed && !caseSummaryEditMode && (
                <>
                  <span className="text-xs text-violet-400 font-medium flex items-center gap-1">
                    <CheckCircle className="w-3 h-3" /> Confirmed
                  </span>
                  <button onClick={() => setCaseSummaryEditMode(true)}
                    className="text-xs text-muted-foreground hover:text-foreground px-2 py-1 rounded-lg hover:bg-secondary/40 transition-colors">
                    Edit
                  </button>
                </>
              )}
            </div>
          </div>

          {/* VIEW MODE — shared structured sections */}
          {!caseSummaryEditMode && (
            <CaseAnalysisSections analysis={editingCaseSummary.analysis} location={editingCaseSummary.location} />
          )}

          {/* EDIT MODE — every section pre-filled & editable; persists exactly as written */}
          {caseSummaryEditMode && (
            <CaseAnalysisEditor
              analysis={editingCaseSummary.analysis}
              location={editingCaseSummary.location}
              onLocation={v => setEditingCaseSummary(c => ({ ...c, location: v }))}
              onChange={na => setEditingCaseSummary(c => ({ ...c, analysis: na }))}
            />
          )}

          {/* Actions — shown when unconfirmed OR in edit mode */}
          {(!caseSummaryConfirmed || caseSummaryEditMode) && (
            <div>
              {showStartOverConfirm ? (
                <div className="p-3 rounded-xl bg-red-500/10 border border-red-500/20 space-y-2">
                  <p className="text-xs text-red-300 font-medium">This will clear your case details and start fresh. Your contact info stays saved.</p>
                  <div className="flex gap-2">
                    <button onClick={startOver} disabled={loading}
                      className="flex-1 py-1.5 rounded-lg bg-red-600 hover:bg-red-500 text-white text-xs font-semibold transition-all disabled:opacity-40">
                      Yes, Start Over
                    </button>
                    <button onClick={() => setShowStartOverConfirm(false)}
                      className="flex-1 py-1.5 rounded-lg bg-secondary/60 hover:bg-secondary text-foreground/80 text-xs font-semibold transition-all">
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div className="space-y-2">
                  {!caseSummaryConfirmed && (
                    <button onClick={confirmCaseSummary}
                      disabled={loading || caseSummaryEditMode}
                      className="w-full py-2.5 rounded-xl bg-violet-600 hover:bg-violet-500 text-white text-sm font-semibold transition-all disabled:opacity-40 flex items-center justify-center gap-2"
                      data-testid="confirm-case-summary-btn">
                      <CheckCircle className="w-4 h-4" /> Confirm
                    </button>
                  )}
                  {caseSummaryEditMode ? (
                    <button onClick={saveCaseSummaryEdits}
                      className="w-full py-2 rounded-xl border border-violet-500/30 bg-violet-500/10 hover:bg-violet-500/20 text-violet-300 text-sm font-medium transition-all flex items-center justify-center gap-2">
                      <CheckCircle className="w-3.5 h-3.5" /> Save Changes
                    </button>
                  ) : (
                    <div className="flex gap-2">
                      <button onClick={() => setCaseSummaryEditMode(true)}
                        className="flex-1 py-2 rounded-xl border border-border/50 bg-secondary/30 hover:bg-secondary/60 text-foreground/80 text-sm font-medium transition-all">
                        Edit
                      </button>
                      <button onClick={() => { setCaseSummaryEditMode(false); setShowStartOverConfirm(true); }}
                        className="flex-1 py-2 rounded-xl border border-red-500/20 bg-red-500/5 hover:bg-red-500/10 text-red-400 text-sm font-medium transition-all">
                        Start Over
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

        </div>
      </div>
    </div>
  );

  // ── Final Review Card (v9.1) — inline, section-by-section edit, then submit ──
  const FinalReviewCard = () => {
    if (!editingFinalReview) return null;
    const e = editingFinalReview;
    const editing = finalReviewEditSection;

    const saveSection = async () => {
      if (!sessionId) return;
      try {
        const res = await axios.post(`${API}/chat/${sessionId}/save-final-review`, {
          contact: e.contact,
          case: { location: e.case.location, fact_summary: e.case.fact_summary, analysis: e.case.analysis },
          call_pref: e.call_pref,
          fee_type: e.fee_type,
        });
        const next = { ...e, case: { ...e.case } };
        if (res.data.analysis) { next.case.analysis = res.data.analysis; next.case.category = res.data.analysis.case_type || next.case.category; }
        setEditingFinalReview(next);
        setFinalReviewData(next);
        setFinalReviewEditSection(null);
      } catch (err) {
      }
    };

    const submitIntake = async () => {
      if (!sessionId) return;
      try {
        // Persist any edits first
        await axios.post(`${API}/chat/${sessionId}/save-final-review`, {
          contact: e.contact,
          case: { location: e.case.location, fact_summary: e.case.fact_summary },
          call_pref: e.call_pref,
          fee_type: e.fee_type,
        });
        // If this is a re-submit after a prior submission, also update the lead record
        if (submitted) {
          await axios.post(`${API}/chat/${sessionId}/update-after-submit`, {
            contact: e.contact,
            case: { location: e.case.location, fact_summary: e.case.fact_summary },
            call_pref: e.call_pref,
            fee_type: e.fee_type,
          });
        }
        // /complete is idempotent — creates the lead the first time, returns submission_data
        const res = await axios.post(`${API}/chat/${sessionId}/complete?user_confirmed=true`);
        const sd = res.data.submission_data || {};
        setSubmitted(true);
        // Send them to the full success page (24h call / 48h attorney office)
        navigate("/intake/success", { state: { leadData: {
          name: sd.contact?.name || e.contact.name,
          phone: sd.contact?.phone || e.contact.phone,
          email: sd.contact?.email || e.contact.email,
          location: sd.location || e.case.location,
          case_category: sd.case_category || e.case.category,
          reference_number: sd.reference_number,
          call_pref: sd.call_pref || e.call_pref,
        }, sessionId } });
      } catch (err) {
        setError('Unable to submit. Please try again.');
      }
    };

    const editMode = editing === 'all';
    const upd = (patch) => setEditingFinalReview(p => ({ ...p, ...patch }));

    return (
      <div className="flex gap-2 sm:gap-3 animate-fade-in" data-testid="final-review-card">
        <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 border bg-primary/20 border-primary/20">
          <span className="text-xs font-bold text-primary">S</span>
        </div>
        <div className="w-full max-w-md">
          <div className="p-4 sm:p-5 rounded-2xl backdrop-blur-sm space-y-4 border border-violet-500/40 bg-card/80">
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <ClipboardCheck className="w-4 h-4 text-violet-400" />
                <h3 className="font-semibold text-sm">Ready to Submit — Final Review</h3>
              </div>
              {submitted && <span className="text-xs text-emerald-400 font-medium flex items-center gap-1"><CheckCircle className="w-3 h-3" /> Submitted</span>}
            </div>
            <div className="text-xs text-muted-foreground">{editMode ? "Make your changes, then Save." : "Review everything below. Tap Edit to make any changes before submitting."}</div>

            {/* Contact */}
            <div className="border-l-2 border-emerald-500/40 pl-3">
              <div className="text-[10px] uppercase tracking-wide text-muted-foreground font-semibold mb-1">Contact</div>
              {editMode ? (
                <div className="space-y-2">
                  <input type="text" value={e.contact.name} onChange={ev => setEditingFinalReview(p => ({ ...p, contact: { ...p.contact, name: ev.target.value } }))} className="w-full px-2 py-1 text-sm rounded bg-secondary/40 border border-border/50" />
                  <input type="tel" value={e.contact.phone} onChange={ev => setEditingFinalReview(p => ({ ...p, contact: { ...p.contact, phone: ev.target.value } }))} className="w-full px-2 py-1 text-sm rounded bg-secondary/40 border border-border/50" />
                  <input type="email" value={e.contact.email} onChange={ev => setEditingFinalReview(p => ({ ...p, contact: { ...p.contact, email: ev.target.value } }))} className="w-full px-2 py-1 text-sm rounded bg-secondary/40 border border-border/50" />
                </div>
              ) : (
                <div className="text-sm text-foreground/90">{e.contact.name}<br />{e.contact.phone}<br />{e.contact.email}</div>
              )}
            </div>

            {/* Case */}
            <div className="border-l-2 border-emerald-500/40 pl-3">
              <div className="text-[10px] uppercase tracking-wide text-muted-foreground font-semibold mb-1">{e.case.category}{e.case.location ? ` · ${e.case.location}` : ''}</div>
              {editMode ? (
                <CaseAnalysisEditor
                  analysis={e.case.analysis}
                  location={e.case.location}
                  onLocation={v => setEditingFinalReview(p => ({ ...p, case: { ...p.case, location: v } }))}
                  onChange={na => setEditingFinalReview(p => ({ ...p, case: { ...p.case, analysis: na } }))}
                />
              ) : (
                <CaseAnalysisSections analysis={e.case.analysis} location={null} />
              )}
            </div>

            {/* Call Pref */}
            <div className="border-l-2 border-emerald-500/40 pl-3">
              <div className="text-[10px] uppercase tracking-wide text-muted-foreground font-semibold mb-1">Call Preference</div>
              {editMode ? (
                <select value={e.call_pref} onChange={ev => upd({ call_pref: ev.target.value })} className="w-full px-2 py-1 text-sm rounded bg-secondary/40 border border-border/50">
                  <option>Early Morning</option><option>Morning</option><option>Early Afternoon</option>
                  <option>Late Afternoon</option><option>Evening</option><option>Flexible</option>
                </select>
              ) : (
                <div className="text-sm">{e.call_pref}</div>
              )}
            </div>

            {/* Fee */}
            <div className="border-l-2 border-emerald-500/40 pl-3">
              <div className="text-[10px] uppercase tracking-wide text-muted-foreground font-semibold mb-1">Fee Preference</div>
              {editMode ? (
                <select value={e.fee_type} onChange={ev => upd({ fee_type: ev.target.value })} className="w-full px-2 py-1 text-sm rounded bg-secondary/40 border border-border/50">
                  <option>Contingency</option><option>Hourly</option><option>Flat fee</option><option>Not sure</option>
                </select>
              ) : (
                <div className="text-sm">{e.fee_type}</div>
              )}
            </div>

            {/* Prior attorney (read-only) */}
            <div className="border-l-2 border-emerald-500/40 pl-3">
              <div className="text-[10px] uppercase tracking-wide text-muted-foreground font-semibold">Prior Attorney</div>
              <div className="text-sm">{e.prior_attorney || 'Not asked'}</div>
            </div>

            {/* Actions — Edit + Submit only (inline edit like the other cards) */}
            {editMode ? (
              <button onClick={saveSection} className="w-full px-4 py-2.5 rounded-lg border border-violet-500/40 bg-violet-500/15 text-violet-200 hover:bg-violet-500/25 transition-colors text-sm font-medium flex items-center justify-center gap-2">
                <CheckCircle className="w-4 h-4" /> Save Changes
              </button>
            ) : (
              <div className="flex gap-2">
                <button onClick={() => setFinalReviewEditSection('all')} className="flex-1 px-4 py-2.5 rounded-lg border border-border/50 bg-secondary/40 text-foreground/80 hover:bg-secondary/60 transition-colors text-sm font-medium flex items-center justify-center gap-2">
                  <Edit className="w-4 h-4" /> Edit
                </button>
                <button onClick={submitIntake} className="flex-1 px-4 py-2.5 rounded-lg bg-emerald-500/20 border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/30 transition-colors text-sm font-medium flex items-center justify-center gap-2" data-testid="submit-intake-btn">
                  <Send className="w-4 h-4" /> {submitted ? "Re-submit" : "Submit"}
                </button>
              </div>
            )}
            <p className="text-xs text-muted-foreground/60 text-center">By submitting, you agree to our <a href="/terms" className="text-primary/60 hover:underline">Terms of Service</a>.</p>
          </div>
        </div>
      </div>
    );
  };

  // ── Submission Confirmation Card (v9.1) — shown after intake is submitted ──
  const SubmissionConfirmationCard = () => {
    if (!submissionData) return null;
    const ts = new Date(submissionData.submitted_at);
    const tsStr = isNaN(ts.getTime()) ? '' : ts.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });

    return (
      <div className="flex gap-2 sm:gap-3 animate-fade-in" data-testid="submission-confirmation-card">
        <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 border bg-emerald-500/20 border-emerald-500/30">
          <CheckCircle className="w-4 h-4 text-emerald-400" />
        </div>
        <div className="w-full max-w-md">
          <div className="p-4 sm:p-5 rounded-2xl backdrop-blur-sm space-y-4 border border-emerald-500/30 bg-card/80">
            <div className="flex justify-between items-center flex-wrap gap-2">
              <div className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-emerald-400" />
                <h3 className="font-semibold text-sm">Your intake has been submitted</h3>
              </div>
              <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-300 border border-emerald-500/20">Ref #{submissionData.reference_number}</span>
            </div>
            {tsStr && <div className="text-xs text-muted-foreground">Submitted {tsStr}</div>}

            <div className="border-l-2 border-emerald-500/40 pl-3">
              <div className="text-[10px] uppercase tracking-wide text-muted-foreground font-semibold mb-2">What happens next</div>
              <div className="space-y-2 text-sm">
                <div className="flex items-start gap-2"><Clock className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" /><span>An attorney will review your case within 48 hours.</span></div>
                <div className="flex items-start gap-2"><Phone className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" /><span>You'll get a call during your preferred time: <strong>{submissionData.call_pref}</strong>.</span></div>
                <div className="flex items-start gap-2"><Mail className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" /><span>A confirmation email is on its way to <strong>{submissionData.contact.email}</strong>.</span></div>
              </div>
            </div>

            <div className="border-l-2 border-emerald-500/40 pl-3">
              <div className="text-[10px] uppercase tracking-wide text-muted-foreground font-semibold mb-1">Submitted Information</div>
              <div className="text-sm">
                <div><strong>{submissionData.contact.name}</strong> · {submissionData.contact.phone} · {submissionData.contact.email}</div>
                <div className="text-muted-foreground mt-1">{submissionData.case_category} · {submissionData.location} · {submissionData.call_pref} · {submissionData.fee_type}</div>
              </div>
            </div>

            <button onClick={() => setSubmissionData(null)} className="w-full px-4 py-2 rounded-lg border border-border/50 text-sm hover:bg-secondary/40 transition-colors flex items-center justify-center gap-2" data-testid="update-info-btn">
              <Edit className="w-4 h-4" /> Update Information
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className={`fixed inset-0 bg-background flex flex-col overflow-hidden ${liveVoice ? 'voice-mode-active' : ''}`} style={{ height: 'var(--app-height, 100dvh)' }} data-testid="chat-page">
      {/* Ambient glow — cohesive with the welcome screen, sits behind all content. */}
      <div className="chat-ambient pointer-events-none absolute inset-0 z-0" aria-hidden="true" />

      {/* ── Dictation tip — a one-time informational notice (NOT a mode selector). Inform → Next → proceed. ── */}
      {!starting && !liveVoice && !dictPromptDismissed && msgCount === 0 && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center px-6 bg-background/80 backdrop-blur-sm animate-fade-in"
          data-testid="dictation-tip"
        >
          <div className="relative w-full max-w-md rounded-3xl border border-border bg-card p-7 shadow-2xl animate-fade-in-up">
            <div className="flex flex-col items-center text-center gap-2 mb-6">
              <div className="w-14 h-14 rounded-2xl bg-primary/15 border border-primary/30 flex items-center justify-center mb-1">
                <Mic className="w-7 h-7 text-primary" />
              </div>
              <h2 className="text-xl font-bold">Quick tip before you start</h2>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Have a long story to tell? When typing your answers, you can tap the
                {" "}<span className="inline-flex items-center align-middle"><Mic className="w-3.5 h-3.5 text-primary mx-0.5" /></span>{" "}
                <strong className="text-foreground">microphone</strong> to dictate instead — we'll turn your speech into text you can review and edit before sending.
              </p>
            </div>

            <button
              autoFocus
              onClick={() => {
                sessionStorage.setItem("dictPromptSeen", "1");
                setDictPromptDismissed(true);
                setModeChoice("text");
              }}
              className="w-full flex items-center justify-center gap-2 p-3.5 rounded-2xl border-2 border-primary bg-primary text-primary-foreground font-semibold text-sm hover:bg-primary/90 transition-all shadow-lg shadow-primary/20"
              data-testid="dictation-tip-next"
            >
              Next <ArrowLeft className="w-4 h-4 rotate-180" />
            </button>
            <p className="text-[11px] text-muted-foreground/60 text-center mt-3">Press Enter to continue</p>
          </div>
        </div>
      )}

      {/* Voice-mode ambiance — frosted vignette + live indicator. pointer-events-none so cards stay tappable. */}
      {liveVoice && (
        <div className="fixed inset-0 z-[15] pointer-events-none" data-testid="voice-ambiance">
          {/* soft frosted edges (blurry but legible — center stays clear) */}
          <div className="absolute inset-0" style={{ backdropFilter: 'blur(2px)', WebkitBackdropFilter: 'blur(2px)', maskImage: 'radial-gradient(ellipse 80% 70% at 50% 45%, transparent 55%, black 100%)', WebkitMaskImage: 'radial-gradient(ellipse 80% 70% at 50% 45%, transparent 55%, black 100%)' }} />
          {/* gentle emerald wash to signal live voice */}
          <div className="absolute inset-0 bg-emerald-500/[0.04]" />
          {/* pulsing top ring */}
          <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-emerald-400/60 to-transparent animate-pulse" />
          {/* floating Live Voice pill */}
          <div className="absolute top-[72px] left-1/2 -translate-x-1/2 flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-500/15 border border-emerald-500/30 backdrop-blur-md shadow-lg">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-400" />
            </span>
            <span className="text-xs font-medium text-emerald-300">Live Voice — Sasha is listening</span>
          </div>
        </div>
      )}
      <Helmet>
        <title>Free Legal Intake — Talk to Sasha | ICantFindALawyer</title>
        <meta name="description" content="Start your free legal intake now. Speak with Sasha, our AI legal assistant, by voice or text. Get matched with the right attorney for your case." />
        <meta name="robots" content="noindex" />
      </Helmet>

      {/* Header */}
      <header className="border-b border-border/30 bg-background/90 backdrop-blur-xl flex-shrink-0 z-20" style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}>
        <div className="max-w-3xl mx-auto px-3 sm:px-4 h-14 sm:h-16 flex items-center justify-between gap-2">
          <button onClick={() => navigate("/")} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-white transition-colors p-1 -ml-1 rounded-lg" data-testid="back-to-home">
            <ArrowLeft className="w-4 h-4" /><span className="hidden sm:inline">Back</span>
          </button>
          <div className="flex items-center gap-2 sm:gap-3">
            <div className={`w-8 h-8 rounded-full border flex items-center justify-center ${liveVoice ? 'bg-emerald-500/30 border-emerald-500/50' : 'bg-primary/20 border-primary/30'}`}>
              <span className="text-xs font-bold text-primary">S</span>
            </div>
            <div>
              <div className="text-sm font-semibold">Sasha</div>
              <div className="flex items-center gap-1">
                <span className={`w-1.5 h-1.5 rounded-full ${liveVoice ? 'bg-emerald-400' : 'bg-emerald-400'} animate-pulse`} />
                <span className="text-xs text-muted-foreground">{liveVoice ? "Live Voice" : "Intake Specialist"}</span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-1 sm:gap-2">
            <button onClick={() => setShowResetConfirm(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-border/60 hover:bg-secondary/40 hover:border-border transition-all text-muted-foreground hover:text-foreground"
              title="Start over" data-testid="reset-conversation-btn">
              <RotateCcw className="w-3.5 h-3.5" />
              <span className="text-xs font-medium">Start Over</span>
            </button>
            <button onClick={() => { setAudioEnabled(a => !a); if (currentAudioRef.current) currentAudioRef.current.pause(); }}
              className="p-2 rounded-lg hover:bg-secondary/40 transition-colors" data-testid="toggle-audio-btn">
              {audioEnabled ? <Volume2 className="w-4 h-4 text-muted-foreground" /> : <VolumeX className="w-4 h-4 text-muted-foreground/40" />}
            </button>
            {/* Header live-voice control — gated Beta. Hidden entirely when VOICE_CALL_ENABLED is off. */}
            {VOICE_CALL_ENABLED && (liveVoice ? (
              <button onClick={stopLiveVoice}
                className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-all border bg-red-500/15 text-red-400 border-red-500/30 hover:bg-red-500/25"
                data-testid="end-live-voice-btn">
                <PhoneOff className="w-3.5 h-3.5" />
                <span className="hidden sm:inline ml-0.5">End</span>
              </button>
            ) : (
              <button onClick={startLiveVoice}
                title={micBlocked ? "Microphone blocked" : "Start live voice"}
                className={`flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-all border ${
                  micBlocked ? "bg-red-500/10 text-red-400 border-red-500/20"
                  : "bg-primary/20 text-primary border-primary/30 hover:bg-primary/30"
                }`}
                data-testid="start-live-voice-btn">
                {micBlocked ? <MicOff className="w-3.5 h-3.5" /> : <Phone className="w-3.5 h-3.5" />}
                <span className="hidden sm:inline ml-0.5">{micBlocked ? "Blocked" : "Sasha"}</span>
              </button>
            ))}
          </div>
        </div>
      </header>

      {/* Mic denied banner */}
      {micBlocked && (
        <div className="bg-red-500/10 border-b border-red-500/20 px-4 py-2.5 flex items-center gap-2 text-xs text-red-400 flex-shrink-0">
          <MicOff className="w-3.5 h-3.5 flex-shrink-0" />
          <span>Microphone blocked. To enable voice: click the <strong>lock icon</strong> in your browser address bar, allow Microphone, then refresh.</span>
          <button onClick={() => setMicPermission("unknown")} className="ml-auto"><X className="w-3.5 h-3.5" /></button>
        </div>
      )}

      {/* Progress Bar */}
      <div className="border-b border-border/20 bg-background/60 px-3 sm:px-4 py-2 flex-shrink-0" data-testid="intake-progress-bar">
        <div className="max-w-3xl mx-auto">
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-xs text-muted-foreground font-medium">
              {intakeReady ? "Complete" : progress.phases[progress.active_phase]?.name || "Contact Info"}
            </span>
            <span className="text-xs text-muted-foreground/60">{progress.percentage}%</span>
          </div>
          <div className="h-1.5 rounded-full bg-secondary/40 overflow-hidden">
            <div
              className="h-full rounded-full transition-all duration-700 ease-out"
              style={{
                width: `${progress.percentage}%`,
                background: intakeReady
                  ? 'hsl(var(--accent))'
                  : `linear-gradient(90deg, hsl(var(--primary)) 0%, hsl(var(--accent)) 100%)`
              }}
            />
          </div>
          <div className="flex justify-between mt-1.5 gap-1">
            {progress.phases.map((p, i) => (
              <div key={i} className="flex-1 text-center">
                <div className={`w-2 h-2 rounded-full mx-auto mb-0.5 transition-colors ${
                  p.complete ? "bg-primary" : i === progress.active_phase ? "bg-primary animate-pulse" : "bg-secondary/40"
                }`} />
                <span className={`text-xs hidden sm:block ${p.complete || i === progress.active_phase ? "text-foreground/70" : "text-muted-foreground/40"}`} style={{ fontSize: '0.6rem' }}>
                  {p.name}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Voice Prompt Banner — gated Beta. Hidden entirely when VOICE_CALL_ENABLED is off. */}
      {VOICE_CALL_ENABLED && msgCount < 2 && !liveVoice && !micBlocked && (
        <div className="px-3 sm:px-4 py-2.5 border-b border-primary/10 bg-primary/5 flex-shrink-0" data-testid="voice-prompt-banner">
          <div className="max-w-3xl mx-auto flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-primary/20 border border-primary/30 flex items-center justify-center flex-shrink-0 animate-pulse">
              <Phone className="w-4 h-4 text-primary" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-foreground">Prefer to talk?</p>
              <p className="text-xs text-muted-foreground">Switch to voice — speak naturally and watch the chat update live</p>
            </div>
            <button
              onClick={startLiveVoice}
              className="px-4 py-2 rounded-full bg-primary text-primary-foreground text-xs font-semibold shadow-sm hover:bg-primary/90 hover:shadow-[0_0_20px_-5px_rgba(99,102,241,0.6)] transition-all flex-shrink-0"
              data-testid="live-conversation-btn">
              Talk to Sasha
            </button>
          </div>
        </div>
      )}

      {/* Messages — flex-1 scrollable middle zone */}
      <main ref={chatContainerRef} className="relative z-10 flex-1 overflow-y-auto overscroll-none px-3 sm:px-4 py-4 space-y-3 sm:space-y-4" style={{ WebkitOverflowScrolling: 'touch' }} data-testid="messages-container">
        {error && (
          <div className="flex items-center gap-2 p-3 rounded-xl bg-destructive/10 border border-destructive/20 text-destructive text-xs sm:text-sm" data-testid="chat-error">
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
            <span className="flex-1">{error}</span>
            <button onClick={() => setError(null)}><X className="w-3.5 h-3.5" /></button>
          </div>
        )}
        <div className="max-w-3xl mx-auto w-full space-y-3 sm:space-y-4">
          {displayMessages.map((msg, i) => (
            <Fragment key={i}>
              {msg.isConfirmation ? (
                <div className="flex justify-center animate-fade-in" data-testid={`message-confirmation-${i}`}>
                  <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-500/10 border border-emerald-500/20">
                    <CheckCircle className="w-3.5 h-3.5 text-emerald-400" />
                    <span className="text-xs text-emerald-400 font-medium">{msg.content}</span>
                  </div>
                </div>
              ) : (
                <div className={`flex gap-2 sm:gap-3 animate-fade-in ${msg.role === "user" ? "flex-row-reverse" : "flex-row"}`} data-testid={`message-${msg.role}-${i}`}>
                  {msg.role === "assistant" && (
                    <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-gradient-to-br from-primary/25 to-primary/5 border border-primary/20 ring-2 ring-primary/5 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-xs font-bold text-primary">S</span>
                    </div>
                  )}
                  <div className={`max-w-[85%] sm:max-w-[80%] flex flex-col gap-1 ${msg.role === "user" ? "items-end" : "items-start"}`}>
                    <div className={`px-3.5 sm:px-4 py-2.5 sm:py-3 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap shadow-md ${msg.role === "user" ? "bg-gradient-to-br from-indigo-500 to-violet-600 text-white rounded-tr-sm shadow-indigo-500/30" : "bg-card text-foreground rounded-tl-sm border border-border/60 shadow-black/20"}`}>
                      {msg.isVoice && msg.role === "user" && <div className="flex items-center gap-1 mb-1 opacity-60"><Mic className="w-3 h-3" /><span className="text-xs">Voice</span></div>}
                      {msg.content}
                    </div>
                    <span className="text-xs text-muted-foreground/50 px-1">{formatTime(msg.timestamp)}</span>
                  </div>
                </div>
              )}
              {/* Contact card — anchored inline so Phase 2+ messages appear below it, not above */}
              {contactData && contactCardMsgIndex !== -1 && i === contactCardMsgIndex && (
                <div className="flex gap-2 sm:gap-3 animate-fade-in" data-testid="contact-confirmation-card">
                  <div className={`w-7 h-7 sm:w-8 sm:h-8 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 border ${contactConfirmed ? "bg-emerald-500/20 border-emerald-500/30" : "bg-primary/20 border-primary/20"}`}>
                    <span className={`text-xs font-bold ${contactConfirmed ? "text-emerald-400" : "text-primary"}`}>S</span>
                  </div>
                  <div className="w-full max-w-sm">
                    <div className={`p-4 sm:p-5 rounded-2xl backdrop-blur-sm space-y-4 border ${contactConfirmed ? "border-emerald-500/30 bg-card/80" : "border-primary/30 bg-card/80"}`}>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <User className={`w-4 h-4 ${contactConfirmed ? "text-emerald-400" : "text-primary"}`} />
                          <h3 className="font-semibold text-sm text-foreground">
                            {contactConfirmed ? "Your Contact Info" : "Confirm Your Information"}
                          </h3>
                        </div>
                        {contactConfirmed && !editingConfirmedContact && (
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-emerald-400 font-medium flex items-center gap-1">
                              <CheckCircle className="w-3 h-3" /> Confirmed
                            </span>
                            <button
                              onClick={() => setEditingConfirmedContact(true)}
                              className="text-xs text-muted-foreground hover:text-foreground px-2 py-1 rounded-lg hover:bg-secondary/40 transition-colors"
                              data-testid="edit-contact-btn">
                              Edit
                            </button>
                          </div>
                        )}
                      </div>
                      <div className="space-y-3">
                        <div>
                          <label className="text-xs text-muted-foreground mb-1 block">Full Name</label>
                          <input type="text" value={editingContact.name || ""} onChange={e => setEditingContact(c => ({ ...c, name: e.target.value }))}
                            readOnly={contactConfirmed && !editingConfirmedContact}
                            className={`w-full px-3 py-2 rounded-lg text-sm focus:outline-none ${contactConfirmed && !editingConfirmedContact ? "bg-secondary/20 border border-border/30 text-foreground/80 cursor-default" : "bg-secondary/40 border border-border/50 focus:ring-2 focus:ring-primary/30"}`}
                            data-testid="contact-name-input" />
                        </div>
                        <div>
                          <label className="text-xs text-muted-foreground mb-1 block">Phone Number</label>
                          <input type="tel" value={editingContact.phone || ""} onChange={e => setEditingContact(c => ({ ...c, phone: formatPhone(e.target.value) }))}
                            readOnly={contactConfirmed && !editingConfirmedContact}
                            className={`w-full px-3 py-2 rounded-lg text-sm focus:outline-none ${contactConfirmed && !editingConfirmedContact ? "bg-secondary/20 border border-border/30 text-foreground/80 cursor-default" : "bg-secondary/40 border border-border/50 focus:ring-2 focus:ring-primary/30"}`}
                            data-testid="contact-phone-input" />
                        </div>
                        <div>
                          <label className="text-xs text-muted-foreground mb-1 block">Email Address</label>
                          <input type="email" value={editingContact.email || ""} onChange={e => setEditingContact(c => ({ ...c, email: e.target.value }))}
                            readOnly={contactConfirmed && !editingConfirmedContact}
                            className={`w-full px-3 py-2 rounded-lg text-sm focus:outline-none ${contactConfirmed && !editingConfirmedContact ? "bg-secondary/20 border border-border/30 text-foreground/80 cursor-default" : "bg-secondary/40 border border-border/50 focus:ring-2 focus:ring-primary/30"}`}
                            data-testid="contact-email-input" />
                        </div>
                      </div>
                      {(!contactConfirmed || editingConfirmedContact) && (
                        <button onClick={confirmContactInfo}
                          disabled={!editingContact.name?.trim() || !editingContact.phone?.trim() || !editingContact.email?.trim()}
                          className="w-full py-2.5 rounded-xl bg-primary hover:bg-primary/90 text-primary-foreground text-sm font-semibold transition-all disabled:opacity-40 flex items-center justify-center gap-2"
                          data-testid="confirm-contact-btn">
                          <CheckCircle className="w-4 h-4" />
                          {editingConfirmedContact ? "Save Changes" : "Confirm & Continue"}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              )}
              {/* Case summary card — anchored inline so new messages appear below it */}
              {caseSummaryData && caseSummaryCardMsgIndex !== -1 && i === caseSummaryCardMsgIndex && (
                <CaseSummaryCard />
              )}
            </Fragment>
          ))}

          {/* Fallback: contact card below all messages if trigger phrase not found in history */}
          {contactData && contactCardMsgIndex === -1 && (
            <div className="flex gap-2 sm:gap-3 animate-fade-in" data-testid="contact-confirmation-card">
              <div className={`w-7 h-7 sm:w-8 sm:h-8 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 border ${contactConfirmed ? "bg-emerald-500/20 border-emerald-500/30" : "bg-primary/20 border-primary/20"}`}>
                <span className={`text-xs font-bold ${contactConfirmed ? "text-emerald-400" : "text-primary"}`}>S</span>
              </div>
              <div className="w-full max-w-sm">
                <div className={`p-4 sm:p-5 rounded-2xl backdrop-blur-sm space-y-4 border ${contactConfirmed ? "border-emerald-500/30 bg-card/80" : "border-primary/30 bg-card/80"}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <User className={`w-4 h-4 ${contactConfirmed ? "text-emerald-400" : "text-primary"}`} />
                      <h3 className="font-semibold text-sm text-foreground">
                        {contactConfirmed ? "Your Contact Info" : "Confirm Your Information"}
                      </h3>
                    </div>
                    {contactConfirmed && !editingConfirmedContact && (
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-emerald-400 font-medium flex items-center gap-1">
                          <CheckCircle className="w-3 h-3" /> Confirmed
                        </span>
                        <button
                          onClick={() => setEditingConfirmedContact(true)}
                          className="text-xs text-muted-foreground hover:text-foreground px-2 py-1 rounded-lg hover:bg-secondary/40 transition-colors"
                          data-testid="edit-contact-btn">
                          Edit
                        </button>
                      </div>
                    )}
                  </div>
                  <div className="space-y-3">
                    <div>
                      <label className="text-xs text-muted-foreground mb-1 block">Full Name</label>
                      <input type="text" value={editingContact.name || ""} onChange={e => setEditingContact(c => ({ ...c, name: e.target.value }))}
                        readOnly={contactConfirmed && !editingConfirmedContact}
                        className={`w-full px-3 py-2 rounded-lg text-sm focus:outline-none ${contactConfirmed && !editingConfirmedContact ? "bg-secondary/20 border border-border/30 text-foreground/80 cursor-default" : "bg-secondary/40 border border-border/50 focus:ring-2 focus:ring-primary/30"}`}
                        data-testid="contact-name-input" />
                    </div>
                    <div>
                      <label className="text-xs text-muted-foreground mb-1 block">Phone Number</label>
                      <input type="tel" value={editingContact.phone || ""} onChange={e => setEditingContact(c => ({ ...c, phone: formatPhone(e.target.value) }))}
                        readOnly={contactConfirmed && !editingConfirmedContact}
                        className={`w-full px-3 py-2 rounded-lg text-sm focus:outline-none ${contactConfirmed && !editingConfirmedContact ? "bg-secondary/20 border border-border/30 text-foreground/80 cursor-default" : "bg-secondary/40 border border-border/50 focus:ring-2 focus:ring-primary/30"}`}
                        data-testid="contact-phone-input" />
                    </div>
                    <div>
                      <label className="text-xs text-muted-foreground mb-1 block">Email Address</label>
                      <input type="email" value={editingContact.email || ""} onChange={e => setEditingContact(c => ({ ...c, email: e.target.value }))}
                        readOnly={contactConfirmed && !editingConfirmedContact}
                        className={`w-full px-3 py-2 rounded-lg text-sm focus:outline-none ${contactConfirmed && !editingConfirmedContact ? "bg-secondary/20 border border-border/30 text-foreground/80 cursor-default" : "bg-secondary/40 border border-border/50 focus:ring-2 focus:ring-primary/30"}`}
                        data-testid="contact-email-input" />
                    </div>
                  </div>
                  {(!contactConfirmed || editingConfirmedContact) && (
                    <button onClick={confirmContactInfo}
                      disabled={!editingContact.name?.trim() || !editingContact.phone?.trim() || !editingContact.email?.trim()}
                      className="w-full py-2.5 rounded-xl bg-primary hover:bg-primary/90 text-primary-foreground text-sm font-semibold transition-all disabled:opacity-40 flex items-center justify-center gap-2"
                      data-testid="confirm-contact-btn">
                      <CheckCircle className="w-4 h-4" />
                      {editingConfirmedContact ? "Save Changes" : "Confirm & Continue"}
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}

          {caseSummaryData && caseSummaryCardMsgIndex === -1 && <CaseSummaryCard />}

          {/* Phase 5 (v9.1): inline Final Review Card — appears once call pref + fee captured */}
          {finalReviewData && !submissionData && <FinalReviewCard />}

          {/* Phase 5 (v9.1): Submission Confirmation Card — appears after submit */}
          {submissionData && <SubmissionConfirmationCard />}

          {/* Inline choice card — appears whenever Sasha asks a fixed-choice question */}
          {uiCard && !loading && !intakeReady && !(caseSummaryData && !caseSummaryConfirmed) && !finalReviewData && !submissionData && (
            <div className="flex gap-2 sm:gap-3 animate-fade-in" data-testid="ui-card">
              <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-primary/20 border border-primary/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-xs font-bold text-primary">S</span>
              </div>
              <div className="max-w-[85%] sm:max-w-[80%]">
                <div className="p-3 sm:p-4 rounded-2xl rounded-tl-sm border border-primary/20 bg-card/80 backdrop-blur-sm flex flex-wrap gap-2">
                  {uiCard.options.map((opt) => (
                    <button
                      key={opt}
                      onClick={() => {
                        // Voice: inject the selection so Sasha hears it & continues. Text: send it.
                        if (liveVoice) injectVoiceConfirmation(`I selected "${opt}" on the screen.`);
                        else sendMessage(opt);
                        setUiCard(null);
                      }}
                      className="px-4 py-2 rounded-xl text-sm font-medium border border-primary/30 bg-secondary/50 text-foreground hover:border-primary/60 hover:bg-primary/15 transition-all active:scale-95"
                      data-testid={`ui-card-option-${opt.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      {opt}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {loading && (
            <div className="flex gap-2 sm:gap-3 animate-fade-in" data-testid="typing-indicator">
              <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-primary/20 border border-primary/20 flex items-center justify-center flex-shrink-0 mt-0.5 sasha-avatar-speaking">
                <span className="text-xs font-bold text-primary">S</span>
              </div>
              <div className="px-4 py-3 rounded-2xl rounded-tl-sm bg-secondary/60 border border-border/30 flex items-center gap-3">
                <div className="sasha-waveform">
                  <div className="bar" /><div className="bar" /><div className="bar" /><div className="bar" /><div className="bar" />
                </div>
                <span className="text-xs text-muted-foreground/60">Sasha is thinking...</span>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>
      </main>

      {/* Input bar — pinned bottom zone */}
      <div className="border-t border-border/30 bg-background/95 backdrop-blur-xl flex-shrink-0 z-30" style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}>
        <div className="max-w-3xl mx-auto px-3 sm:px-4 py-2.5 sm:py-3 space-y-2">
          {liveVoice ? (
            /* ── Live Voice Mode: WebRTC control panel ── */
            <div className="space-y-2">
              <div className="flex items-center gap-3 p-3 rounded-xl border border-emerald-500/20 bg-emerald-500/5">
                {/* Voice orb with waveform */}
                <div className="relative flex items-center justify-center flex-shrink-0">
                  {liveStatus === "speaking" && (
                    <div className="absolute rounded-full bg-primary/15 animate-ping" style={{ width: 56, height: 56, animationDuration: "2s" }} />
                  )}
                  {liveStatus === "listening" && (
                    <div className="absolute rounded-full bg-emerald-500/15 animate-ping" style={{ width: 56, height: 56, animationDuration: "2.5s" }} />
                  )}
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center transition-all duration-300 ${
                    liveStatus === "speaking" ? "bg-primary/30 border-2 border-primary/50 sasha-avatar-speaking"
                    : liveStatus === "listening" ? "bg-emerald-500/20 border-2 border-emerald-500/40"
                    : liveStatus === "thinking" ? "bg-amber-500/15 border-2 border-amber-500/30"
                    : liveStatus === "connecting" ? "bg-blue-500/15 border-2 border-blue-500/30"
                    : "bg-secondary/30 border border-border/40"
                  }`}>
                    {liveStatus === "thinking" ? (
                      <div className="w-5 h-5 border-2 border-primary/40 border-t-primary rounded-full animate-spin" />
                    ) : liveStatus === "speaking" ? (
                      <div className="sasha-waveform" style={{ height: 18 }}>
                        <div className="bar" style={{ background: 'hsl(var(--primary))' }} />
                        <div className="bar" style={{ background: 'hsl(var(--primary))' }} />
                        <div className="bar" style={{ background: 'hsl(var(--primary))' }} />
                        <div className="bar" style={{ background: 'hsl(var(--primary))' }} />
                        <div className="bar" style={{ background: 'hsl(var(--primary))' }} />
                      </div>
                    ) : liveStatus === "connecting" ? (
                      <div className="w-5 h-5 border-2 border-blue-400/40 border-t-blue-400 rounded-full animate-spin" />
                    ) : (
                      <Mic className="w-5 h-5 text-emerald-400" />
                    )}
                  </div>
                </div>
                {/* Status text */}
                <div className="flex-1 min-w-0">
                  <p className={`text-sm font-medium ${
                    liveStatus === "speaking" ? "text-primary"
                    : liveStatus === "listening" ? "text-emerald-400"
                    : liveStatus === "thinking" ? "text-amber-400"
                    : liveStatus === "connecting" ? "text-blue-400"
                    : "text-muted-foreground"
                  }`}>
                    {liveStatus === "speaking" ? "Sasha is speaking..."
                    : liveStatus === "listening" ? "Listening — speak naturally"
                    : liveStatus === "thinking" ? "Processing..."
                    : liveStatus === "connecting" ? "Connecting..."
                    : "Starting..."}
                  </p>
                  <p className="text-xs text-muted-foreground/60 mt-0.5">Real-time voice — instant responses</p>
                </div>
                {/* End call button */}
                <button onClick={stopLiveVoice}
                  className="px-4 py-2.5 rounded-lg bg-red-500/15 border border-red-500/30 text-red-400 text-sm font-medium hover:bg-red-500/25 transition-all flex items-center gap-2 flex-shrink-0"
                  data-testid="end-live-voice-inline">
                  <PhoneOff className="w-4 h-4" />
                  <span>End</span>
                </button>
              </div>
            </div>
          ) : (
            <>
            {/* Voice-to-text nudge — only on the "describe what happened" step (Incident Details = phase index 2). */}
            {progress.active_phase === 2 && uiMode === "chatting" && (
              <div className="flex items-center gap-2.5 mb-2 px-3 py-2 rounded-xl border border-primary/30 bg-primary/10 animate-fade-in" data-testid="voice-hint">
                <Mic className="w-4 h-4 text-primary flex-shrink-0" />
                <p className="text-xs text-foreground/90 leading-snug">
                  This part can be long — it's often easier to <strong className="text-primary">tap the purple mic</strong> below and just talk instead of typing.
                </p>
              </div>
            )}
            <div className="flex gap-2 items-end flex-nowrap">
              {/* Composer call button — gated Beta. Hidden when VOICE_CALL_ENABLED is off; dictation + text input stay. */}
              {VOICE_CALL_ENABLED && (
                <button onClick={startLiveVoice}
                  className={`flex items-center gap-1 px-2 py-2.5 rounded-lg text-xs font-medium transition-all border flex-shrink-0 ${
                    micBlocked ? "bg-red-500/10 text-red-400 border-red-500/20" : "bg-primary/20 text-primary border-primary/30 hover:bg-primary/30"
                  }`}
                  title={micBlocked ? "Microphone blocked" : "Call Sasha"}
                  data-testid="call-sasha-btn">
                  {micBlocked ? <PhoneOff className="w-3.5 h-3.5" /> : <Phone className="w-3.5 h-3.5" />}
                  <span className="ml-0.5 hidden sm:inline">{micBlocked ? "Blocked" : "Sasha"}</span>
                </button>
              )}
              {/* Dictation: speak your message into the box (separate from the live call) */}
              <button onClick={toggleDictation} disabled={transcribing || uiMode !== "chatting"}
                className={`w-9 h-9 sm:w-11 sm:h-11 rounded-xl flex items-center justify-center transition-all flex-shrink-0 disabled:opacity-40 border ${
                  dictating ? "bg-red-500/20 border-red-500/40 text-red-400 animate-pulse" : "bg-primary/15 border-primary/40 hover:bg-primary/25 text-primary"
                }`}
                title={dictating ? "Stop dictation" : transcribing ? "Transcribing…" : "Dictate your message"}
                data-testid="dictate-btn">
                {transcribing ? <div className="w-4 h-4 border-2 border-primary/60 border-t-transparent rounded-full animate-spin" />
                  : dictating ? <span className="w-3 h-3 rounded-sm bg-red-400" />
                  : <Mic className="w-4 h-4" />}
              </button>
              <input ref={fileInputRef} type="file" className="hidden"
                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.gif,.txt,.xlsx,.xls,.csv"
                onChange={handleFileUpload} data-testid="file-input" />
              <button onClick={() => fileInputRef.current?.click()} disabled={uploading || !sessionId}
                className="w-9 h-9 sm:w-11 sm:h-11 rounded-xl bg-secondary/40 border border-border/50 hover:bg-secondary/60 flex items-center justify-center transition-all flex-shrink-0 disabled:opacity-40"
                title="Upload document" data-testid="upload-file-btn">
                {uploading ? <div className="w-4 h-4 border-2 border-primary/60 border-t-transparent rounded-full animate-spin" /> : <Paperclip className="w-4 h-4 text-muted-foreground" />}
              </button>
              <textarea ref={inputRef} value={input} onChange={(e) => { setInput(e.target.value); if (uiCard) setUiCard(null); }} onKeyDown={handleKeyDown}
                placeholder={uiMode === "card:contact" ? "Confirm your contact info above to continue" : uiMode === "card:summary" ? "Review and confirm your summary above to continue" : "Type your message..."}
                rows={1} enterKeyHint="send"
                disabled={uiMode !== "chatting"}
                className="flex-1 min-w-0 px-3 sm:px-4 py-2.5 sm:py-3 rounded-xl bg-secondary/40 border border-border/50 focus:outline-none focus:ring-2 focus:ring-primary/30 resize-none transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                style={{ maxHeight: '100px' }} data-testid="chat-input"
                onInput={(e) => { e.target.style.height = 'auto'; e.target.style.height = Math.min(e.target.scrollHeight, 100) + 'px'; }} />
              <button
                onClick={() => {
                  // Mid-dictation Send: stop recording, then transcribe + auto-send (handled in mr.onstop).
                  if (dictating) { sendAfterTranscribeRef.current = true; stopDictation(); }
                  else sendMessage();
                }}
                disabled={loading || transcribing || uiMode !== "chatting" || (!input.trim() && !dictating)}
                className="w-9 h-9 sm:w-11 sm:h-11 rounded-xl bg-primary hover:bg-primary/90 text-primary-foreground flex items-center justify-center transition-all disabled:opacity-40 flex-shrink-0"
                data-testid="send-btn"><Send className="w-4 h-4" /></button>
            </div>
            </>
          )}
        </div>
      </div>

      {/* Reset Confirmation Dialog */}
      {showResetConfirm && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" data-testid="reset-confirm-overlay">
          <div className="bg-background border border-border rounded-2xl p-6 max-w-sm w-full shadow-2xl" data-testid="reset-confirm-dialog">
            <h3 className="text-lg font-semibold text-foreground mb-2">Start Over?</h3>
            <p className="text-sm text-muted-foreground mb-6">This will clear the current conversation and begin a new intake. Your previous session data is still saved.</p>
            <div className="flex gap-3">
              <button onClick={() => setShowResetConfirm(false)}
                className="flex-1 px-4 py-2.5 rounded-xl border border-border text-sm font-medium hover:bg-secondary/40 transition-all"
                data-testid="reset-cancel-btn">
                Cancel
              </button>
              <button onClick={resetConversation}
                className="flex-1 px-4 py-2.5 rounded-xl bg-red-500/15 border border-red-500/30 text-red-400 text-sm font-medium hover:bg-red-500/25 transition-all"
                data-testid="reset-confirm-btn">
                Start Over
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Contact info overlay — shows on first visit, sits over everything */}
      {showContactOverlay && (
        <ContactOverlay onConfirm={handleOverlayConfirm} />
      )}
    </div>
  );
}
